import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import { ClipboardList, BarChart3, Coffee, Utensils, LogOut, Plus, Check, X } from 'lucide-react';
import JTDiscussionPage from './JTDiscussionPage';

const API = process.env.REACT_APP_API_URL || '';
const authHeader = () => ({ Authorization: `Token ${localStorage.getItem('Token')}` });

// ── Color helpers ──────────────────────────────────────────────────────────────
const COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#0f766e'];
const colorFor = (name) => COLORS[(name || '').charCodeAt(0) % COLORS.length];

// ── Avatar ─────────────────────────────────────────────────────────────────────
const STATUS_COLOR = { online: '#16a34a', busy: '#f59e0b', away: '#f97316', offline: '#64748b', tea_break: '#d97706', lunch_break: '#f43f5e', see_you_tomorrow: '#818cf8', on_a_break: '#f97316' };

function Avatar({ name, size = 36, status, photo_url }) {
  const initials = (name || '?').split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
  const dotSize = Math.max(14, Math.round(size * 0.38));
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      {photo_url ? (
        <img src={photo_url} alt={initials} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: '50%', background: colorFor(name),
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size * 0.38, fontWeight: 700, userSelect: 'none',
        }}>{initials}</div>
      )}
      {status && (
        status === 'offline'
          ? <span style={{ position: 'absolute', bottom: -2, right: -2, width: dotSize, height: dotSize, borderRadius: '50%', background: '#e2e8f0', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dotSize * 0.55, fontWeight: 900, color: '#64748b', lineHeight: 1 }}>✕</span>
          : status === 'tea_break'
            ? <span style={{ position: 'absolute', bottom: -2, right: -2, width: dotSize, height: dotSize, borderRadius: '50%', background: '#fef3c7', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dotSize * 0.62, lineHeight: 1 }}>🍵</span>
            : status === 'see_you_tomorrow'
            ? <span style={{ position: 'absolute', bottom: -2, right: -2, width: dotSize, height: dotSize, borderRadius: '50%', background: '#ede9fe', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dotSize * 0.62, lineHeight: 1 }}>🌅</span>
            : status === 'on_a_break'
            ? <span style={{ position: 'absolute', bottom: -2, right: -2, width: dotSize, height: dotSize, borderRadius: '50%', background: '#fef3c7', border: '3px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dotSize * 0.62, lineHeight: 1 }}>☕</span>
            : <span style={{ position: 'absolute', bottom: -2, right: -2, width: dotSize, height: dotSize, borderRadius: '50%', background: STATUS_COLOR[status] || STATUS_COLOR.offline, border: '3px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }} />
      )}
    </div>
  );
}

// ── Group avatar ───────────────────────────────────────────────────────────────
function GroupAvatar({ size = 36 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#4f46e5',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
    }}>G</div>
  );
}

// ── Day divider ────────────────────────────────────────────────────────────────
function DayDivider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0 12px', gap: 12, padding: '0 16px' }}>
      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      <span style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap', background: '#fff', padding: '2px 10px', border: '1px solid #e5e7eb', borderRadius: 12 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
    </div>
  );
}

function dayLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const diff = Math.floor((today - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const REACTION_EMOJIS = ['👍','❤️','😂','😮','😢','👏','🔥','✅'];

// ── Message bubble ─────────────────────────────────────────────────────────────
function PollWidget({ poll, msgId, myUserId, onVote, isMine, userMap, onShare }) {
  const [showVoters, setShowVoters] = React.useState(false);
  const votes = poll.votes || {};
  const totalVotes = Object.keys(votes).length;
  const myVote = votes[String(myUserId)];
  return (
    <div style={{ minWidth: 180 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 6, opacity: 0.7 }}>📊 POLL</div>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, lineHeight: 1.4 }}>{poll.question}</div>
      {(poll.options || []).map((opt, i) => {
        const count = Object.values(votes).filter(v => v === i).length;
        const pct = totalVotes > 0 ? Math.round(count / totalVotes * 100) : 0;
        const chosen = myVote === i;
        return (
          <div key={i} onClick={() => onVote(msgId, i)}
            style={{ marginBottom: 7, cursor: 'pointer', borderRadius: 8, border: `1.5px solid ${chosen ? (isMine ? '#fff' : '#4f46e5') : (isMine ? 'rgba(255,255,255,0.3)' : '#e2e8f0')}`, overflow: 'hidden', background: isMine ? (chosen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)') : (chosen ? '#ede9fe' : '#fff') }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', position: 'relative', zIndex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: chosen ? 700 : 500, flex: 1 }}>{chosen ? '✓ ' : ''}{opt}</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.75, flexShrink: 0, marginLeft: 8 }}>{pct}% · {count}</span>
            </div>
            <div style={{ height: 3, background: isMine ? 'rgba(255,255,255,0.15)' : '#f1f5f9' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: isMine ? 'rgba(255,255,255,0.6)' : '#4f46e5', transition: 'width 0.3s' }} />
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: 11, marginTop: 4, opacity: 0.6 }}>{totalVotes} vote{totalVotes !== 1 ? 's' : ''} · tap an option to vote</div>

      {/* Voter visibility + share — only shown to poll creator */}
      {isMine && totalVotes > 0 && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${isMine ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}`, paddingTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={e => { e.stopPropagation(); setShowVoters(v => !v); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: isMine ? '#134e4a' : '#4f46e5', fontFamily: 'inherit', padding: 0 }}>
              {showVoters ? '▲ Hide voters' : '▼ Who voted'}
            </button>
            {onShare && (
              <button onClick={e => {
                e.stopPropagation();
                const lines = (poll.options || []).map((opt, i) => {
                  const count = Object.values(votes).filter(v => v === i).length;
                  const pct = totalVotes > 0 ? Math.round(count / totalVotes * 100) : 0;
                  const voterNames = Object.entries(votes)
                    .filter(([, v]) => v === i)
                    .map(([uid]) => (userMap && userMap[uid]) || `User ${uid}`)
                    .join(', ');
                  return `${opt} — ${pct}% (${count})${voterNames ? ' · ' + voterNames : ''}`;
                });
                const text = `📊 <b>Poll Results: ${poll.question}</b><br>${lines.join('<br>')}<br><i>${totalVotes} total vote${totalVotes !== 1 ? 's' : ''}</i>`;
                onShare(text);
              }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#134e4a', fontFamily: 'inherit', padding: 0, opacity: 0.75 }}>
                📤 Share results
              </button>
            )}
          </div>
          {showVoters && (
            <div style={{ marginTop: 6 }}>
              {(poll.options || []).map((opt, i) => {
                const voters = Object.entries(votes).filter(([, v]) => v === i).map(([uid]) => (userMap && userMap[uid]) || `User ${uid}`);
                if (voters.length === 0) return null;
                return (
                  <div key={i} style={{ marginBottom: 5 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, opacity: 0.75, marginBottom: 2 }}>{opt}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {voters.map((name, j) => (
                        <span key={j} style={{ fontSize: 10.5, background: isMine ? 'rgba(19,78,74,0.15)' : '#ede9fe', color: isMine ? '#134e4a' : '#4f46e5', borderRadius: 10, padding: '1px 7px', fontWeight: 500 }}>{name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg, onReply, onEdit, onDelete, onReact, onForward, onVote, onShare, myUserId, userMap }) {
  const [hovered, setHovered] = React.useState(false);
  const [showReactPicker, setShowReactPicker] = React.useState(false);
  const time = new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  // System / scheduled reminder messages — centered banner, no sender attribution
  if (msg.is_system) {
    return (
      <div data-msg-id={msg.id} style={{ display: 'flex', justifyContent: 'center', padding: '6px 16px', marginBottom: 4 }}>
        <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#eff6ff)', border: '1px solid #d1fae5', borderRadius: 16, padding: '8px 18px', fontSize: 13, color: '#0f766e', fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', maxWidth: 520, textAlign: 'center' }}>
          {msg.content.includes('\n') ? (
            <>
              <div>{msg.content.split('\n')[0]}</div>
              <div style={{ fontSize: 12, fontWeight: 400, color: '#374151', marginTop: 3 }}>{msg.content.split('\n').slice(1).join('\n')}</div>
            </>
          ) : <span>{msg.content}</span>}
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>{time}</span>
        </div>
      </div>
    );
  }

  const reactions = msg.reactions || {};
  const hasReactions = Object.keys(reactions).length > 0;

  // Emoji picker strip — floats above wherever it's anchored
  const EmojiPickerStrip = ({ side }) => (
    <div style={{
      position: 'absolute', bottom: 28, [side === 'right' ? 'right' : 'left']: 0,
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 24,
      padding: '5px 8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 300,
      display: 'flex', gap: 2, whiteSpace: 'nowrap',
    }}>
      {REACTION_EMOJIS.map(e => (
        <button key={e} onClick={() => { onReact(msg.id, e); setShowReactPicker(false); }}
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.1s' }}
          onMouseEnter={ev => { ev.currentTarget.style.background = '#f1f5f9'; ev.currentTarget.style.transform = 'scale(1.25)'; }}
          onMouseLeave={ev => { ev.currentTarget.style.background = 'none'; ev.currentTarget.style.transform = 'scale(1)'; }}>
          {e}
        </button>
      ))}
    </div>
  );

  // Reaction pills — absolutely positioned on bottom edge of bubble
  const ReactionRow = ({ side }) => hasReactions ? (
    <div style={{
      position: 'absolute', bottom: -14, [side === 'right' ? 'right' : 'left']: 8,
      display: 'flex', gap: 3, zIndex: 2,
    }}>
      {Object.entries(reactions).map(([emoji, data]) => (
        <button key={emoji} onClick={() => onReact(msg.id, emoji)}
          title={data.users.join(', ')}
          style={{
            border: data.mine ? '1.5px solid #14b8a6' : '1.5px solid #d1d5db',
            background: data.mine ? 'rgba(20,184,166,0.08)' : '#fff',
            borderRadius: 12, padding: '1px 7px', cursor: 'pointer',
            fontSize: 13, display: 'flex', alignItems: 'center', gap: 3,
            lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}>
          {emoji}
          <span style={{ fontSize: 11, fontWeight: 700, color: data.mine ? '#0d9488' : '#475569' }}>{data.count}</span>
        </button>
      ))}
      <div style={{ position: 'relative' }}>
        <button onClick={() => setShowReactPicker(v => !v)}
          style={{ border: '1.5px solid #d1d5db', background: '#fff', borderRadius: 12, padding: '1px 7px', cursor: 'pointer', fontSize: 13, color: '#94a3b8', lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>+</button>
        {showReactPicker && <EmojiPickerStrip side={side} />}
      </div>
    </div>
  ) : null;

  const actionBtn = (label, onClick, color = '#64748b') => (
    <button onClick={onClick}
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 7px', borderRadius: 6, fontSize: 13, color, lineHeight: 1 }}
      title={label}>{label}</button>
  );

  const QuotedBar = () => msg.quoted_message ? (
    <div onClick={() => {
      const el = document.querySelector(`[data-msg-id="${msg.quoted_message.id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'background 0.2s';
      el.style.background = 'rgba(99,102,241,0.12)';
      setTimeout(() => { el.style.background = ''; }, 1500);
    }} style={{ cursor: 'pointer', borderLeft: `3px solid ${msg.is_mine ? '#14b8a6' : '#94a3b8'}`, paddingLeft: 8, marginBottom: 6, opacity: 0.9, borderRadius: 2 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: msg.is_mine ? '#0d9488' : '#475569', marginBottom: 1 }}>{msg.quoted_message.sender_name}</div>
      <div style={{ fontSize: 12, color: msg.is_mine ? '#0f766e' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{msg.quoted_message.content}</div>
    </div>
  ) : null;

  const reactBtn = (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setShowReactPicker(v => !v)}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 7px', borderRadius: 6, fontSize: 13, color: '#64748b', lineHeight: 1 }}
        title="React">😊</button>
      {showReactPicker && !hasReactions && <EmojiPickerStrip side={msg.is_mine ? 'right' : 'left'} />}
    </div>
  );

  const actions = (
    <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', opacity: hovered ? 1 : 0, transition: 'opacity 0.12s', pointerEvents: hovered ? 'auto' : 'none', padding: '2px 4px', gap: 0 }}>
      {reactBtn}
      {actionBtn('↩', () => onReply(msg))}
      {actionBtn('⤷', () => onForward && onForward(msg), '#0d9488')}
      {msg.is_mine && actionBtn('✏', () => onEdit(msg))}
      {msg.is_mine && actionBtn('🗑', () => onDelete(msg.id), '#ef4444')}
    </div>
  );

  if (msg.is_mine) {
    return (
      <div data-msg-id={msg.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: hasReactions ? 20 : 6, padding: '0 16px', alignItems: 'flex-start', gap: 6 }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        {actions}
        <div style={{ maxWidth: msg.content?.includes('<table') ? '90%' : '70%' }}>
          <div style={{ textAlign: 'right', fontSize: 11.5, color: '#0f172a', fontWeight: 600, marginBottom: 3 }}>You <span style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 400 }}>{time}{msg.edited && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>· edited</span>}</span></div>
          <div style={{ position: 'relative' }}>
            <div className="dm-bubble-own" style={{ background: '#e8edff', color: '#1e1b4b', padding: '8px 14px', borderRadius: '8px 2px 8px 8px', fontSize: 14, wordBreak: 'break-word', lineHeight: 1.55, overflowX: 'auto', border: '1px solid #c7d2fe' }}>
              <QuotedBar />
              {msg.poll_data
                ? <PollWidget poll={msg.poll_data} msgId={msg.id} myUserId={myUserId} onVote={onVote} isMine={true} userMap={userMap} onShare={onShare} />
                : <div dangerouslySetInnerHTML={{ __html: msg.content }} />}
            </div>
            <ReactionRow side="right" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-msg-id={msg.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: hasReactions ? 20 : 6, padding: '0 16px' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <Avatar name={msg.sender_name} size={32} />
      <div style={{ maxWidth: msg.content?.includes('<table') ? '90%' : '70%' }}>
        <div style={{ fontSize: 12.5, color: '#0f172a', fontWeight: 700, marginBottom: 3 }}>{msg.sender_name} <span style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 400 }}>{time}{msg.edited && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>· edited</span>}</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ position: 'relative' }}>
            <div className="dm-bubble" style={{ background: '#ffffff', color: '#1e293b', padding: '8px 14px', borderRadius: '2px 8px 8px 8px', fontSize: 14, wordBreak: 'break-word', lineHeight: 1.55, overflowX: 'auto', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              <QuotedBar />
              {msg.poll_data
                ? <PollWidget poll={msg.poll_data} msgId={msg.id} myUserId={myUserId} onVote={onVote} isMine={false} userMap={userMap} onShare={null} />
                : <div dangerouslySetInnerHTML={{ __html: msg.content }} />}
            </div>
            <ReactionRow side="left" />
          </div>
          {actions}
        </div>
      </div>
    </div>
  );
}

// ── Thread row in sidebar ──────────────────────────────────────────────────────
function ThreadRow({ thread, active, userStatuses, onClick }) {
  const name = thread.is_group
    ? (thread.group_name || thread.participants.map((p) => p.name).join(', '))
    : (thread.participants[0]?.name || 'Unknown');
  const last = thread.last_message;
  const unread = thread.unread_count || 0;
  const participantEmail = !thread.is_group ? thread.participants[0]?.email : null;
  const status = participantEmail ? (userStatuses[participantEmail] || 'offline') : null;
  const lastTime = last ? new Date(last.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px 9px 12px',
        cursor: 'pointer', margin: '1px 0',
        borderLeft: active ? '3px solid #14b8a6' : '3px solid transparent',
        background: active ? '#f0fdfa' : 'transparent',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {thread.is_group ? <GroupAvatar size={40} /> : <Avatar name={name} size={40} status={status} photo_url={thread.participants[0]?.photo_url} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: unread ? 700 : 600, fontSize: 14, color: unread ? '#0f172a' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
          <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, marginLeft: 6 }}>{lastTime}</span>
        </div>
        {status && !thread.is_group && (
          <div style={{ marginTop: 2 }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
              background: status === 'online' ? 'rgba(22,163,74,0.1)' : status === 'busy' ? 'rgba(245,158,11,0.1)' : status === 'away' ? 'rgba(249,115,22,0.1)' : status === 'offline' ? 'rgba(100,116,139,0.1)' : status === 'tea_break' ? 'rgba(217,119,6,0.12)' : status === 'lunch_break' ? 'rgba(244,63,94,0.12)' : status === 'see_you_tomorrow' ? 'rgba(129,140,248,0.15)' : 'rgba(249,115,22,0.12)',
              color: STATUS_COLOR[status] || STATUS_COLOR.offline }}>
              {status === 'online' ? 'Online' : status === 'offline' ? 'Offline' : status === 'away' ? 'Away' : status === 'busy' ? 'Busy' : status === 'tea_break' ? '🍵 Tea Break' : status === 'lunch_break' ? '🍱 Lunch Break' : status === 'see_you_tomorrow' ? '🌅 Left for the Day' : '☕ On a Break'}
            </span>
          </div>
        )}
        {participantEmail && (
          <div style={{ fontSize: 10.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
            {participantEmail}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
          <span style={{ fontSize: 12.5, color: unread ? '#374151' : '#9ca3af', fontWeight: unread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {last ? `${last.sender_name}: ${last.content}` : 'No messages yet'}
          </span>
          {unread > 0 && (
            <span style={{ background: '#14b8a6', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10.5, fontWeight: 700, flexShrink: 0, marginLeft: 6 }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main MessagesPage ──────────────────────────────────────────────────────────
export default function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputEmpty, setInputEmpty] = useState(true);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasOlderMsgs, setHasOlderMsgs] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState({});
  const [threadSearch, setThreadSearch] = useState('');
  const [sidebarTab, setSidebarTab] = useState('recent'); // 'recent' | 'people'
  const [filterOnline, setFilterOnline] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [showAttach, setShowAttach] = useState(false);
  const [attachUrl, setAttachUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Group settings & task update
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [groupSettingsTab, setGroupSettingsTab] = useState('features');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showPollForm, setShowPollForm] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [memberAddSearch, setMemberAddSearch] = useState('');
  const [showAddMemberPanel, setShowAddMemberPanel] = useState(false);
  const [reminderSaving, setReminderSaving] = useState({});
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlertEmoji, setNewAlertEmoji] = useState('🔔');
  const [newAlertLabel, setNewAlertLabel] = useState('');
  const [newAlertContent, setNewAlertContent] = useState('');
  const [newAlertTime, setNewAlertTime] = useState('');
  const [newAlertType, setNewAlertType] = useState('daily'); // 'daily' | 'repeat' | 'once'
  const [newAlertRepeatHours, setNewAlertRepeatHours] = useState('1');
  const [newAlertDate, setNewAlertDate] = useState('');
  const [alertSaving, setAlertSaving] = useState(false);

  // Projects tab
  const [projectsTab, setProjectsTab] = useState([]); // project list for sidebar
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [projectsTabSearch, setProjectsTabSearch] = useState('');

  // Project discussion panel (right panel when a project is selected)
  const [activeProject, setActiveProject] = useState(null);
  const [projDiscussions, setProjDiscussions] = useState([]);
  const [projLoading, setProjLoading] = useState(false);
  const [projInput, setProjInput] = useState('');
  const [projSending, setProjSending] = useState(false);
  const [taskMode, setTaskMode] = useState('morning'); // 'morning' | 'eod'
  const [taskRows, setTaskRows] = useState([{ project: '', task: '', hoursPlanned: '', hoursActual: '', status: 'Done' }]);
  const [projectList, setProjectList] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [activeProjectRow, setActiveProjectRow] = useState(null);

  // Modal states
  const [modal, setModal] = useState(null); // 'new-dm' | 'new-group'
  const [userSearch, setUserSearch] = useState('');
  const [groupSelected, setGroupSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  // Forward-message state
  const [forwardMsg, setForwardMsg] = useState(null);
  const [forwardTab, setForwardTab] = useState('people');
  const [forwardSearch, setForwardSearch] = useState('');
  const [forwardSegments, setForwardSegments] = useState([]);
  const [forwardSegmentsLoading, setForwardSegmentsLoading] = useState(false);
  const [forwarding, setForwarding] = useState(false);

  const bottomRef = useRef(null);
  const msgAreaRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null); // contentEditable ref
  const isNearBottomRef = useRef(true); // true while user is within 120px of bottom
  const lastMsgIdRef = useRef(null);    // ID of the newest message we've loaded
  const oldestMsgIdRef = useRef(null);  // ID of the oldest message currently in view
  const myNameRef = useRef(null);       // current user's display name (for optimistic reactions)
  const projLastIdRef = useRef(null);
  const projMsgAreaRef = useRef(null);
  const projPollRef = useRef(null);
  const imageInputRef = useRef(null);

  const scrollToBottom = (smooth = false) => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' }), 50);
  };

  // ── Fetch helpers ────────────────────────────────────────────────────────────
  const fetchThreads = useCallback(async (attempt = 0) => {
    try {
      const { data } = await axios.get(`${API}/api/dm/threads/`, { headers: authHeader() });
      if (Array.isArray(data)) setThreads(data);
    } catch (err) {
      if (err?.response?.status === 429 && attempt < 5) {
        setTimeout(() => fetchThreads(attempt + 1), (attempt + 1) * 3000);
      }
    }
  }, []); // eslint-disable-line

  const fetchMessages = useCallback(async (threadId, attempt = 0) => {
    lastMsgIdRef.current = null;
    oldestMsgIdRef.current = null;
    if (attempt === 0) { setMessages([]); setHasOlderMsgs(false); setLoadingMsgs(true); }
    try {
      const { data } = await axios.get(`${API}/api/dm/threads/${threadId}/messages/`, { headers: authHeader() });
      const msgs = data.messages || [];
      setMessages(msgs);
      hasPollRef.current = msgs.some(m => m.poll_data);
      setHasOlderMsgs(data.has_more || false);
      if (msgs.length > 0) {
        lastMsgIdRef.current = msgs[msgs.length - 1].id;
        oldestMsgIdRef.current = msgs[0].id;
        if (!myNameRef.current) {
          const mine = msgs.find(m => m.is_mine);
          if (mine) myNameRef.current = mine.sender_name;
        }
      }
      scrollToBottom(false);
    } catch (err) {
      const is429 = err?.response?.status === 429;
      if (is429 && attempt < 3) {
        setTimeout(() => fetchMessages(threadId, attempt + 1), (attempt + 1) * 2000);
        return;
      }
      setMessages([{ _error: true }]);
    } finally {
      setLoadingMsgs(false);
    }
  }, []); // eslint-disable-line

  // Refresh poll_data on existing poll messages so vote counts stay live
  const hasPollRef = useRef(false);
  const refreshPollVotes = useCallback(async (threadId) => {
    if (!hasPollRef.current) return;
    try {
      const { data } = await axios.get(`${API}/api/dm/threads/${threadId}/messages/`, { headers: authHeader() });
      const map = {};
      (data.messages || []).forEach(m => { if (m.poll_data) map[m.id] = m.poll_data; });
      if (Object.keys(map).length === 0) return;
      setMessages(prev => prev.map(m => map[m.id] ? { ...m, poll_data: map[m.id] } : m));
    } catch {}
  }, []);

  // Incremental poll — only fetches messages newer than lastMsgIdRef
  const pollBackoffRef = useRef(0); // consecutive 429 count
  const pollMessages = useCallback(async (threadId) => {
    if (!lastMsgIdRef.current) return;
    // Back off if we've been rate-limited recently
    if (pollBackoffRef.current > 0) {
      pollBackoffRef.current--;
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/dm/threads/${threadId}/messages/?after=${lastMsgIdRef.current}`,
        { headers: authHeader() }
      );
      pollBackoffRef.current = 0;
      const newMsgs = data.messages || [];
      if (newMsgs.length > 0) {
        setMessages(prev => {
          const seen = new Set(prev.map(m => m.id));
          const deduped = newMsgs.filter(m => !seen.has(m.id));
          return deduped.length ? [...prev, ...deduped] : prev;
        });
        if (newMsgs.some(m => m.poll_data)) hasPollRef.current = true;
        lastMsgIdRef.current = newMsgs[newMsgs.length - 1].id;
        if (!myNameRef.current) {
          const mine = newMsgs.find(m => m.is_mine);
          if (mine) myNameRef.current = mine.sender_name;
        }
        if (isNearBottomRef.current) scrollToBottom(false);
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        pollBackoffRef.current = Math.min((pollBackoffRef.current || 0) + 3, 12); // skip 3–12 ticks
      }
    }
  }, []);

  // Load older messages when user scrolls up
  const fetchOlderMessages = useCallback(async () => {
    if (!oldestMsgIdRef.current || loadingOlder) return;
    setLoadingOlder(true);
    const scrollEl = msgAreaRef.current;
    const prevScrollHeight = scrollEl?.scrollHeight || 0;
    try {
      const { data } = await axios.get(
        `${API}/api/dm/threads/${activeThread.id}/messages/?before=${oldestMsgIdRef.current}`,
        { headers: authHeader() }
      );
      const olderMsgs = data.messages || [];
      if (olderMsgs.length > 0) {
        setMessages(prev => [...olderMsgs, ...prev]);
        oldestMsgIdRef.current = olderMsgs[0].id;
        setHasOlderMsgs(data.has_more || false);
        // Restore scroll position so viewport doesn't jump
        setTimeout(() => {
          if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight - prevScrollHeight;
        }, 0);
      } else {
        setHasOlderMsgs(false);
      }
    } catch {}
    setLoadingOlder(false);
  }, [activeThread, loadingOlder]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await api.get('users/user-list/');
      const users = Array.isArray(res.data) ? res.data : res.data.users || [];
      setAllUsers(users.map((u) => ({
        id: u.id,
        name: u.name || (`${u.first_name || ''} ${u.last_name || ''}`.trim()) || u.username,
        email: u.email,
        username: u.username,
      })));
    } catch {}
  }, []);

  const fetchUserStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/users/statuses/`, { headers: authHeader() });
      if (res.ok) setUserStatuses(await res.json());
    } catch {}
  }, []);

  // ── On mount ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchThreads();
    fetchAllUsers();
    fetchUserStatuses();
    const id = setInterval(fetchUserStatuses, 10000);
    return () => clearInterval(id);
  }, [fetchThreads, fetchAllUsers, fetchUserStatuses]);

  // ── Handle navigation state threadId (from Operations mini chat panel) ────────
  const handledThreadIdRef = useRef(null);
  useEffect(() => {
    const threadId = location.state?.threadId;
    if (threadId && threadId !== handledThreadIdRef.current && threads.length > 0) {
      handledThreadIdRef.current = threadId;
      const t = threads.find(th => th.id === threadId);
      if (t) {
        selectThread(t);
        // Use navigate to properly clear React Router state (window.replaceState won't)
        navigate('/messages', { replace: true, state: {} });
      }
    }
  }, [location.state?.threadId, threads.length]); // eslint-disable-line

  // ── Handle ?dm=userId from avatar clicks ─────────────────────────────────────
  const handledDmUserIdRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('dm');
    if (userId && userId !== handledDmUserIdRef.current && allUsers.length > 0) {
      handledDmUserIdRef.current = userId;
      const user = allUsers.find((u) => String(u.id) === userId);
      if (user) { openDMWith(user); navigate('/messages', { replace: true }); }
    }
  }, [location.search, allUsers.length]); // eslint-disable-line

  // ── Load projects when projects tab is selected ───────────────────────────────
  useEffect(() => {
    if (sidebarTab === 'projects' && projectsTab.length === 0 && !projectsLoading) {
      setProjectsLoading(true);
      fetch(`${API}/api/dm/projects/`, { headers: authHeader() })
        .then(r => r.json()).then(d => { setProjectsTab(Array.isArray(d) ? d : []); })
        .catch(() => {}).finally(() => setProjectsLoading(false));
    }
  }, [sidebarTab]); // eslint-disable-line

  // ── Load project discussions when a project is selected ──────────────────────
  useEffect(() => {
    clearInterval(projPollRef.current);
    if (!activeProject) { setProjDiscussions([]); return; }
    setProjLoading(true);
    setProjDiscussions([]);
    projLastIdRef.current = null;
    axios.get(`${API}/api/discussion/list/`, { params: { segment: activeProject.id }, headers: authHeader() })
      .then(({ data }) => {
        const msgs = data.messages || [];
        setProjDiscussions(msgs);
        if (msgs.length) projLastIdRef.current = msgs[msgs.length - 1].id;
        setTimeout(() => { if (projMsgAreaRef.current) projMsgAreaRef.current.scrollTop = projMsgAreaRef.current.scrollHeight; }, 80);
      })
      .catch(() => {})
      .finally(() => setProjLoading(false));
    projPollRef.current = setInterval(async () => {
      try {
        const params = { segment: activeProject.id };
        if (projLastIdRef.current) params.after = projLastIdRef.current;
        const { data } = await axios.get(`${API}/api/discussion/list/`, { params, headers: authHeader() });
        const msgs = data.messages || [];
        if (msgs.length) {
          setProjDiscussions(prev => [...prev, ...msgs]);
          projLastIdRef.current = msgs[msgs.length - 1].id;
          setTimeout(() => { if (projMsgAreaRef.current) projMsgAreaRef.current.scrollTop = projMsgAreaRef.current.scrollHeight; }, 80);
        }
      } catch {}
    }, 10000);
    return () => clearInterval(projPollRef.current);
  }, [activeProject?.id]); // eslint-disable-line

  // ── Poll active thread ────────────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(pollRef.current);
    if (activeThread) {
      let tickCount = 0;
      pollRef.current = setInterval(() => {
        tickCount++;
        pollMessages(activeThread.id);
        if (tickCount % 3 === 0) refreshPollVotes(activeThread.id); // refresh poll votes every 9s
        if (tickCount % 5 === 0) fetchThreads(); // fetch threads every 5th tick (~15s)
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [activeThread, pollMessages, refreshPollVotes, fetchThreads]);

  // ── Thread actions ────────────────────────────────────────────────────────────
  const selectThread = (thread) => {
    setActiveProject(null);
    setActiveThread(thread);
    fetchMessages(thread.id);
    fetchThreads();
    setTimeout(() => inputRef.current?.focus(), 100);
    // Refresh DM badge after messages are fetched+marked-read on the backend (~600ms grace period)
    setTimeout(() => window.dispatchEvent(new Event('jtRefreshDm')), 600);
  };

  const openDMWith = async (user) => {
    setCreating(true);
    setActiveProject(null);
    try {
      // Prefer the already-loaded thread (same object Recent tab uses) to avoid
      // the POST endpoint's cnt=2 filter returning a different/empty thread.
      const existing = threads.find(t =>
        !t.is_group && t.participants.some(p => p.id === user.id)
      );
      let thread;
      if (existing) {
        thread = existing;
      } else {
        const { data } = await axios.post(`${API}/api/dm/threads/`, {
          participant_ids: [user.id], is_group: false,
        }, { headers: authHeader() });
        await fetchThreads();
        thread = data;
      }
      setActiveThread(thread);
      fetchMessages(thread.id);
      setModal(null);
    } catch {}
    setCreating(false);
  };

  const createGroup = async () => {
    if (groupSelected.length < 2) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/dm/threads/`, {
        participant_ids: groupSelected.map((u) => u.id),
        is_group: true,
        group_name: groupName.trim() || groupSelected.map((u) => u.name).join(', '),
      }, { headers: authHeader() });
      await fetchThreads();
      setActiveThread(data);
      fetchMessages(data.id);
      setModal(null);
      setGroupSelected([]);
      setGroupName('');
    } catch {}
    setCreating(false);
  };

  const execFmt = (cmd, val) => {
    document.execCommand(cmd, false, val || null);
    inputRef.current?.focus();
  };

  // Intercept paste — detect Excel table and render as clean HTML table
  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(it => it.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;
      setImageUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      axios.post(`${API}/api/dm/upload-image/`, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      }).then(({ data }) => {
        if (data.url && inputRef.current) {
          inputRef.current.focus();
          document.execCommand('insertHTML', false, `<img src="${data.url}" alt="image" style="max-width:320px;max-height:320px;border-radius:8px;display:block;margin:4px 0;">`);
          setInputEmpty(false);
        }
      }).catch(() => alert('Image upload failed. Please try again.'))
        .finally(() => setImageUploading(false));
      return;
    }
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html && html.includes('<table')) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const table = doc.querySelector('table');
      if (table) {
        table.removeAttribute('class');
        table.style.cssText = 'border-collapse:collapse;font-size:12.5px;margin:4px 0;max-width:100%;';
        table.querySelectorAll('td,th').forEach(cell => {
          const rawColor = cell.style.color;
          const fw = cell.tagName === 'TH' ? '700' : (cell.style.fontWeight || '');
          const bg = cell.style.background || cell.style.backgroundColor || '';
          const colorLc = rawColor.toLowerCase().replace(/\s/g,'');
          const isWhiteText = !rawColor || colorLc === 'white' || colorLc === '#ffffff' || colorLc === 'rgb(255,255,255)' || colorLc === '#fff';
          const color = isWhiteText ? '' : rawColor;
          cell.removeAttribute('class');
          cell.style.cssText = `border:1px solid #d1d5db;padding:4px 10px;text-align:left;white-space:nowrap;${color ? 'color:' + color + ';' : ''}${fw ? 'font-weight:' + fw + ';' : ''}`;
          const bgLc = bg.toLowerCase().replace(/\s/g,'');
          if (bg && bgLc !== 'white' && bgLc !== '#ffffff' && bgLc !== 'rgb(255,255,255)') cell.style.background = bg;
        });
        table.querySelectorAll('tr').forEach(r => { r.removeAttribute('class'); r.style.cssText = ''; });
        document.execCommand('insertHTML', false, table.outerHTML);
        setInputEmpty(false);
        return;
      }
    }
    document.execCommand('insertText', false, text);
    if (text.trim()) setInputEmpty(false);
  };

  const sendMessage = async () => {
    const raw = inputRef.current?.innerHTML || '';
    const html = raw.replace(/^<br\s*\/?>$/i, '').trim();
    if (!html || !activeThread || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      if (editingMsg) {
        const { data: updated } = await axios.patch(
          `${API}/api/dm/threads/${activeThread.id}/messages/${editingMsg.id}/`,
          { content: html }, { headers: authHeader(), timeout: 15000 }
        );
        setMessages(prev => prev.map(m => m.id === updated.id ? updated : m));
        setEditingMsg(null);
      } else {
        const { data: newMsg } = await axios.post(
          `${API}/api/dm/threads/${activeThread.id}/messages/`,
          { content: html, quoted_message_id: replyTo?.id || null },
          { headers: authHeader(), timeout: 15000 }
        );
        setMessages(prev => prev.some(m => m.id === newMsg.id) ? prev : [...prev, newMsg]);
        lastMsgIdRef.current = newMsg.id;
        if (!myNameRef.current && newMsg.is_mine) myNameRef.current = newMsg.sender_name;
        setReplyTo(null);
        scrollToBottom(true);
      }
      if (inputRef.current) { inputRef.current.innerHTML = ''; }
      setInputEmpty(true);
      fetchThreads();
    } catch (err) {
      console.error('Send failed:', err);
    } finally {
      sendingRef.current = false;
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10 MB'); return; }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await axios.post(`${API}/api/dm/upload-image/`, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      if (data.url && inputRef.current) {
        inputRef.current.focus();
        document.execCommand('insertHTML', false, `<img src="${data.url}" alt="image" style="max-width:320px;max-height:320px;border-radius:8px;display:block;margin:4px 0;">`);
        setInputEmpty(false);
      }
    } catch (err) {
      alert('Image upload failed. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const startEdit = (msg) => {
    setReplyTo(null);
    setEditingMsg(msg);
    if (inputRef.current) {
      inputRef.current.innerHTML = msg.content;
      setInputEmpty(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const deleteMessage = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/api/dm/threads/${activeThread.id}/messages/${msgId}/`, { headers: authHeader() });
      setMessages(prev => prev.filter(m => m.id !== msgId));
      fetchThreads();
    } catch {}
  };

  const cancelAction = () => {
    setReplyTo(null);
    setEditingMsg(null);
    if (inputRef.current) { inputRef.current.innerHTML = ''; }
    setInputEmpty(true);
  };

  const openForward = useCallback(async (msg) => {
    setForwardMsg(msg);
    setForwardTab('people');
    setForwardSearch('');
    setForwardSegments([]);
    setForwardSegmentsLoading(true);
    try {
      const em = localStorage.getItem('username');
      const namesRes = await axios.get(`${API}/users/cascading_names/`, { params: { email: em }, headers: authHeader() });
      const names = namesRes.data || [];
      if (names.length > 0) {
        const params = new URLSearchParams();
        names.forEach(n => params.append('name', n));
        const segsRes = await axios.get(`${API}/api/segments/fetch-segments-by-name/?${params.toString()}`, { headers: authHeader() });
        setForwardSegments(Array.isArray(segsRes.data) ? segsRes.data : []);
      }
    } catch {}
    setForwardSegmentsLoading(false);
  }, []);

  const doForward = useCallback(async (target) => {
    if (!forwardMsg || forwarding) return;
    const plainText = forwardMsg.content.replace(/<[^>]*>/g, '').trim();
    const forwardedHtml = `<em style="color:#64748b;font-size:12px">Forwarded from chat:</em><br/>${forwardMsg.content}`;
    setForwarding(true);
    try {
      if (target.type === 'thread') {
        await axios.post(`${API}/api/dm/threads/${target.id}/messages/`, { content: forwardedHtml }, { headers: authHeader() });
      } else if (target.type === 'discussion') {
        const em = localStorage.getItem('username');
        await api.post('discussion/create/', { segment: target.id, userId: em, comment: `Forwarded from chat:\n${plainText}`, send_email: false }, { headers: authHeader() });
      }
      setForwardMsg(null);
    } catch {}
    setForwarding(false);
  }, [forwardMsg, forwarding]);

  const toggleReaction = async (msgId, emoji) => {
    if (!activeThread) return;
    // Optimistic update — one emoji per user
    setMessages(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      let reactions = { ...(m.reactions || {}) };
      const me = myNameRef.current || 'Me';
      const clicking = reactions[emoji];
      if (clicking?.mine) {
        // Toggle off: remove our vote from this emoji
        const newCount = clicking.count - 1;
        if (newCount <= 0) {
          const r = { ...reactions }; delete r[emoji]; return { ...m, reactions: r };
        }
        reactions[emoji] = { ...clicking, count: newCount, mine: false, users: clicking.users.filter(u => u !== me) };
      } else {
        // Remove our vote from whichever other emoji we had (if any)
        Object.keys(reactions).forEach(e => {
          if (e !== emoji && reactions[e]?.mine) {
            const prev2 = reactions[e];
            const newCount = prev2.count - 1;
            if (newCount <= 0) { delete reactions[e]; }
            else reactions[e] = { ...prev2, count: newCount, mine: false, users: prev2.users.filter(u => u !== me) };
          }
        });
        // Add our vote to the new emoji
        reactions[emoji] = { count: (clicking?.count || 0) + 1, mine: true, users: [...(clicking?.users || []), me] };
      }
      return { ...m, reactions };
    }));
    try {
      await axios.post(`${API}/api/dm/threads/${activeThread.id}/messages/${msgId}/reactions/`, { emoji }, { headers: authHeader() });
    } catch {}
  };

  // ── Group settings & Daily Task Update ───────────────────────────────────────
  const myUserId = (() => { try { return JSON.parse(atob(localStorage.getItem('Token').split('.')[1] || '') || '{}').user_id; } catch { return null; } })();

  const saveReminderTime = async (key, time) => {
    if (!activeThread) return;
    setReminderSaving(prev => ({ ...prev, [key]: 'saving' }));
    try {
      const { data } = await axios.patch(
        `${API}/api/dm/threads/${activeThread.id}/settings/`,
        { [key]: time },
        { headers: authHeader() }
      );
      setThreads(prev => prev.map(t => t.id === activeThread.id ? { ...t, group_settings: data.group_settings } : t));
      setActiveThread(prev => prev ? { ...prev, group_settings: data.group_settings } : prev);
      window.dispatchEvent(new Event('teaBreakTimeSaved'));
      setReminderSaving(prev => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => setReminderSaving(prev => ({ ...prev, [key]: '' })), 2000);
    } catch {
      setReminderSaving(prev => ({ ...prev, [key]: '' }));
    }
  };

  const saveCustomAlerts = async (alerts) => {
    if (!activeThread) return;
    try {
      const { data } = await axios.patch(
        `${API}/api/dm/threads/${activeThread.id}/settings/`,
        { custom_alerts: alerts },
        { headers: authHeader() }
      );
      setThreads(prev => prev.map(t => t.id === activeThread.id ? { ...t, group_settings: data.group_settings } : t));
      setActiveThread(prev => prev ? { ...prev, group_settings: data.group_settings } : prev);
    } catch {}
  };

  const toggleGroupFeature = async (feature, enabled) => {
    if (!activeThread) return;
    try {
      const { data } = await axios.patch(
        `${API}/api/dm/threads/${activeThread.id}/settings/`,
        { features: { [feature]: enabled } },
        { headers: authHeader() }
      );
      setThreads(prev => prev.map(t => t.id === activeThread.id ? { ...t, group_settings: data.group_settings } : t));
      setActiveThread(prev => prev ? { ...prev, group_settings: data.group_settings } : prev);
    } catch {}
  };

  const openTaskForm = async (mode = 'morning') => {
    setShowTaskForm(true);
    setTaskRows([{ project: '', task: '', hoursPlanned: '', hoursActual: '', status: 'Done' }]);
    setTaskMode(mode);
    if (projectList.length === 0) {
      try {
        const { data } = await axios.get(`${API}/api/dm/projects/`, { headers: authHeader() });
        setProjectList(Array.isArray(data) ? data : (data.results || []));
      } catch { setProjectList([]); }
    }
  };

  const addTaskRow = () => setTaskRows(prev => [...prev, { project: '', task: '', hoursPlanned: '', hoursActual: '', status: 'Done' }]);
  const removeTaskRow = (i) => setTaskRows(prev => prev.filter((_, idx) => idx !== i));
  const updateTaskRow = (i, field, value) => setTaskRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const submitTaskUpdate = async () => {
    const validRows = taskRows.filter(r => r.project.trim() || r.task.trim());
    if (!validRows.length || !activeThread) return;
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const isEod = taskMode === 'eod';
    const label = isEod ? "Today's Summary" : 'Morning Task Plan';
    const emoji = isEod ? '🌅' : '📋';
    const headerRow = isEod
      ? '<tr><th>Project Name</th><th>Task Completed</th><th>Hours Spent</th><th>Status</th></tr>'
      : '<tr><th>Project Name</th><th>Task to be done</th><th>Approx. Hours needed</th></tr>';
    const rows = isEod
      ? validRows.map(r => `<tr><td>${r.project}</td><td>${r.task}</td><td>${r.hoursActual}</td><td>${r.status}</td></tr>`).join('')
      : validRows.map(r => `<tr><td>${r.project}</td><td>${r.task}</td><td>${r.hoursPlanned}</td></tr>`).join('');
    const content = `<b>${emoji} ${label} — ${today}</b><br><table>${headerRow}${rows}</table>`;
    try {
      const { data: newMsg } = await axios.post(`${API}/api/dm/threads/${activeThread.id}/messages/`, { content }, { headers: authHeader() });
      setMessages(prev => [...prev, newMsg]);
      lastMsgIdRef.current = newMsg.id;
      scrollToBottom(true);
      fetchThreads();
      setShowTaskForm(false);
      setTaskRows([{ project: '', task: '', hoursPlanned: '', hoursActual: '', status: 'Done' }]);
      if (isEod) {
        // Auto-set status to "Left for the day"
        const newStatus = 'see_you_tomorrow';
        localStorage.setItem('myStatus', newStatus);
        window.dispatchEvent(new CustomEvent('myStatusChanged', { detail: newStatus }));
        fetch(`${API}/users/set-status/`, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        }).catch(() => {});
        // Send EOD email to group members
        fetch(`${API}/api/dm/send-eod/`, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }).catch(() => {});
      }
    } catch {}
  };

  // ── Poll creation ─────────────────────────────────────────────────────────────
  const openPollForm = () => {
    setShowPollForm(true);
    setPollQuestion('');
    setPollOptions(['', '']);
  };

  const submitPoll = async () => {
    const validOpts = pollOptions.map(o => o.trim()).filter(Boolean);
    if (!pollQuestion.trim() || validOpts.length < 2 || !activeThread) return;
    try {
      const { data: newMsg } = await axios.post(
        `${API}/api/dm/threads/${activeThread.id}/messages/`,
        { content: `📊 Poll: ${pollQuestion}`, poll_data: { question: pollQuestion.trim(), options: validOpts, votes: {} } },
        { headers: authHeader() }
      );
      setMessages(prev => [...prev, newMsg]);
      hasPollRef.current = true;
      lastMsgIdRef.current = newMsg.id;
      scrollToBottom(true);
      fetchThreads();
      setShowPollForm(false);
    } catch {}
  };

  const handleVote = async (msgId, optionIndex) => {
    if (!activeThread) return;
    try {
      const { data } = await axios.post(
        `${API}/api/dm/threads/${activeThread.id}/messages/${msgId}/vote/`,
        { option_index: optionIndex },
        { headers: authHeader() }
      );
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, poll_data: data.poll_data } : m));
    } catch {}
  };

  const handleSharePoll = async (htmlContent) => {
    if (!activeThread) return;
    try {
      const { data: newMsg } = await axios.post(
        `${API}/api/dm/threads/${activeThread.id}/messages/`,
        { content: htmlContent, quoted_message_id: null },
        { headers: authHeader() }
      );
      setMessages(prev => [...prev, newMsg]);
      lastMsgIdRef.current = newMsg.id;
      scrollToBottom(true);
      fetchThreads();
    } catch {}
  };

  // ── Member management ─────────────────────────────────────────────────────────
  const addMember = async (userId) => {
    if (!activeThread) return;
    try {
      const { data } = await axios.post(
        `${API}/api/dm/threads/${activeThread.id}/members/`,
        { user_id: userId },
        { headers: authHeader() }
      );
      setActiveThread(data);
      setThreads(prev => prev.map(t => t.id === activeThread.id ? data : t));
      setMemberAddSearch('');
    } catch {}
  };

  const removeMember = async (userId) => {
    if (!activeThread) return;
    const isSelf = userId === myUserId;
    if (!window.confirm(isSelf ? 'Leave this group?' : 'Remove this member from the group?')) return;
    try {
      const { data } = await axios.delete(
        `${API}/api/dm/threads/${activeThread.id}/members/`,
        { data: { user_id: userId }, headers: authHeader() }
      );
      if (data.left) {
        setActiveThread(null);
        fetchThreads();
      } else {
        setActiveThread(data);
        setThreads(prev => prev.map(t => t.id === activeThread.id ? data : t));
      }
    } catch (e) {
      if (e.response?.data?.error) alert(e.response.data.error);
    }
  };

  // ── @mention detection & insertion ──────────────────────────────────────────
  const detectMention = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) { setShowMentions(false); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { setShowMentions(false); return; }
    const text = node.textContent.slice(0, range.startOffset);
    const atIdx = text.lastIndexOf('@');
    if (atIdx === -1) { setShowMentions(false); return; }
    const query = text.slice(atIdx + 1);
    if (/\s/.test(query)) { setShowMentions(false); return; }
    setMentionQuery(query);
    setMentionIdx(0);
    setShowMentions(true);
  };

  const insertMention = (user) => {
    inputRef.current?.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return;
    const offset = range.startOffset;
    const atIdx = node.textContent.lastIndexOf('@', offset - 1);
    if (atIdx === -1) return;

    // Remove @query text
    const del = document.createRange();
    del.setStart(node, atIdx);
    del.setEnd(node, offset);
    del.deleteContents();

    // Insert styled mention span
    const span = document.createElement('span');
    span.textContent = `@${user.name}`;
    span.style.cssText = 'color:#0d9488;font-weight:600;background:rgba(20,184,166,0.1);padding:0 3px;border-radius:3px;';
    span.contentEditable = 'false';
    span.dataset.mentionId = user.id;
    const curRange = window.getSelection().getRangeAt(0);
    curRange.insertNode(span);

    // Place cursor after the span with a space
    const space = document.createTextNode(' ');
    span.after(space);
    const nr = document.createRange();
    nr.setStartAfter(space);
    nr.collapse(true);
    sel.removeAllRanges();
    sel.addRange(nr);

    setShowMentions(false);
    setMentionQuery('');
    setInputEmpty(false);
  };

  // ── Attachment link insertion ────────────────────────────────────────────────
  const insertAttachment = () => {
    const raw = attachUrl.trim();
    if (!raw) return;

    const isLocalPath = !raw.match(/^https?:\/\//i);
    if (isLocalPath) {
      alert('Local file paths (e.g. C:\\... or E:\\OneDrive\\...) cannot be opened by other users.\n\nPlease share the OneDrive/SharePoint web link instead:\n1. Right-click the file in OneDrive\n2. Select "Share" → "Copy link"\n3. Paste that https:// link here.');
      return;
    }

    // Extract filename from the URL
    let noQuery;
    try { noQuery = decodeURIComponent(raw.split('?')[0].split('#')[0]); } catch { noQuery = raw.split('?')[0]; }
    const parts = noQuery.replace(/\\/g, '/').split('/');
    const filename = parts[parts.length - 1] || 'Attachment';

    // Pick icon by extension
    const ext = filename.split('.').pop().toLowerCase();
    const icon = ['xlsx','xls','csv'].includes(ext) ? '📊'
      : ['docx','doc'].includes(ext) ? '📄'
      : ['pptx','ppt'].includes(ext) ? '📊'
      : ['pdf'].includes(ext) ? '📕'
      : ['zip','rar','7z'].includes(ext) ? '🗜️'
      : ['jpg','jpeg','png','gif','webp'].includes(ext) ? '🖼️'
      : '📎';

    // Escape href for safe HTML attribute embedding
    const safeHref = raw.replace(/"/g, '%22').replace(/'/g, '%27');
    const chip = `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" `
      + `style="display:inline-flex;align-items:center;gap:6px;background:rgba(79,70,229,0.07);`
      + `border:1.5px solid rgba(79,70,229,0.25);border-radius:8px;padding:5px 12px;`
      + `color:#4f46e5;text-decoration:none;font-size:13px;font-weight:500;margin:2px 0;">`
      + `${icon} ${filename}</a>`;

    inputRef.current?.focus();
    document.execCommand('insertHTML', false, chip);
    setInputEmpty(false);
    setShowAttach(false);
    setAttachUrl('');
  };

  // ── Derived ───────────────────────────────────────────────────────────────────
  const mentionUsers = showMentions
    ? allUsers.filter(u => (u.name || '').toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  // Deduplicate DM threads by participant email — keep the one with the most recent message
  const _allDms = threads.filter((t) => !t.is_group);
  const _dmByEmail = new Map();
  _allDms.forEach((t) => {
    const email = t.participants[0]?.email || `id_${t.id}`;
    const prev = _dmByEmail.get(email);
    if (!prev) { _dmByEmail.set(email, t); return; }
    const tTime = t.last_message ? new Date(t.last_message.created_at).getTime() : 0;
    const pTime = prev.last_message ? new Date(prev.last_message.created_at).getTime() : 0;
    if (tTime > pTime) _dmByEmail.set(email, t);
  });
  const dms = Array.from(_dmByEmail.values());
  const groups = threads.filter((t) => t.is_group);
  const totalDmUnread = dms.reduce((s, t) => s + (t.unread_count || 0), 0);
  const totalGroupUnread = groups.reduce((s, t) => s + (t.unread_count || 0), 0);
  const q = threadSearch.toLowerCase();
  const filteredDms = dms.filter((t) => {
    if (q && !(t.participants[0]?.name || '').toLowerCase().includes(q)) return false;
    if (filterUnread && !(t.unread_count > 0)) return false;
    if (filterOnline) {
      const email = t.participants[0]?.email;
      if (!email || (userStatuses[email] || 'offline') !== 'online') return false;
    }
    return true;
  });
  const filteredGroups = groups.filter((t) => {
    if (q && !(t.group_name || t.participants.map((p) => p.name).join(', ')).toLowerCase().includes(q)) return false;
    if (filterUnread && !(t.unread_count > 0)) return false;
    return true;
  });

  // Users who don't yet have a DM thread — always visible in "People" section
  const dmParticipantIds = new Set(dms.flatMap((t) => t.participants.map((p) => p.id)));
  const filteredPeople = allUsers.filter((u) =>
    !dmParticipantIds.has(u.id) && (!q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
  );

  const activeName = activeThread
    ? activeThread.is_group
      ? (activeThread.group_name || activeThread.participants.map((p) => p.name).join(', '))
      : (activeThread.participants[0]?.name || 'Chat')
    : '';

  const filteredUserSearch = allUsers.filter((u) =>
    (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  // Group messages by day
  const msgsByDay = [];
  let lastDay = '';
  messages.filter(m => !m._error).forEach((m) => {
    const day = new Date(m.created_at).toDateString();
    if (day !== lastDay) { msgsByDay.push({ type: 'divider', label: dayLabel(m.created_at), key: day }); lastDay = day; }
    msgsByDay.push({ type: 'msg', msg: m, key: m.id });
  });

  const activeParticipantEmail = activeThread && !activeThread.is_group ? activeThread.participants[0]?.email : null;
  const activeStatus = activeParticipantEmail ? (userStatuses[activeParticipantEmail] || 'offline') : null;
  const activeOnline = activeStatus === 'online';
  const activeParticipantName = activeThread && !activeThread.is_group ? activeThread.participants[0]?.name : null;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 100px)', overflow: 'hidden', fontFamily: 'inherit', paddingTop: 18 }}>
      <style>{`
        .dm-bubble-own span[data-mention-id] {
          color: #5c3d1e !important;
          background: rgba(255,255,255,0.55) !important;
          font-weight: 700 !important;
        }
      `}</style>

      {/* ── Left sidebar ─────────────────────────────────────────────────────── */}
      <div style={{ width: 320, flexShrink: 0, background: '#fff', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0' }}>

        {/* Sidebar header */}
        <div style={{ background: '#fff', padding: '16px 16px 12px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.3px' }}>Chat</span>
            <button onClick={() => { setGroupSelected([]); setGroupName(''); setModal('new-group'); }} title="New group"
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, color: '#475569', cursor: 'pointer', padding: '6px 12px', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
              + Group
            </button>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 4, marginBottom: 10 }}>
            {[['recent', 'Recent', totalDmUnread], ['groups', 'Groups', totalGroupUnread], ['people', 'People', 0], ['projects', 'Projects', 0]].map(([tab, label, badge]) => (
              <button key={tab} onClick={() => setSidebarTab(tab)}
                style={{ flex: 1, padding: '5px 0', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 12.5, fontWeight: 600, position: 'relative',
                  background: sidebarTab === tab ? '#fff' : 'transparent',
                  color: sidebarTab === tab ? '#0f172a' : '#64748b',
                  boxShadow: sidebarTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>
                {label}
                {badge > 0 && (
                  <span style={{ position: 'absolute', top: 2, right: 4, minWidth: 14, height: 14, padding: '0 3px', background: '#ef4444', color: '#fff', borderRadius: 7, fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          {sidebarTab !== 'projects' && (
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#94a3b8' }}>🔍</span>
              <input
                type="text"
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder={sidebarTab === 'people' ? 'Search people…' : sidebarTab === 'groups' ? 'Search groups…' : 'Search conversations…'}
                style={{ width: '100%', padding: '8px 12px 8px 30px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a', fontSize: 12.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
            </div>
          )}
          {/* Filter chips — hidden on Projects tab */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {sidebarTab !== 'projects' && (
              <button onClick={() => setFilterOnline((v) => !v)}
                style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: filterOnline ? '1px solid #16a34a' : '1px solid #e2e8f0', background: filterOnline ? 'rgba(22,163,74,0.1)' : '#f8fafc', color: filterOnline ? '#16a34a' : '#64748b' }}>
                ● Online
              </button>
            )}
            {(sidebarTab === 'recent' || sidebarTab === 'groups') && (
              <button onClick={() => setFilterUnread((v) => !v)}
                style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: filterUnread ? '1px solid #14b8a6' : '1px solid #e2e8f0', background: filterUnread ? 'rgba(20,184,166,0.1)' : '#f8fafc', color: filterUnread ? '#0d9488' : '#64748b' }}>
                ✉ Unread
              </button>
            )}
          </div>
        </div>

        {/* Thread / People list — dark scrollbar */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
          {sidebarTab === 'recent' ? (
            (() => {
              // Show only DMs sorted by most recent message
              const allRecent = [...filteredDms].sort((a, b) => {
                const at = a.last_message ? new Date(a.last_message.created_at).getTime() : 0;
                const bt = b.last_message ? new Date(b.last_message.created_at).getTime() : 0;
                return bt - at;
              });
              return allRecent.length > 0
                ? allRecent.map((t) => (
                    <ThreadRow key={t.id} thread={t} active={activeThread?.id === t.id} userStatuses={userStatuses} onClick={() => selectThread(t)} />
                  ))
                : <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: 12.5 }}>
                    {threadSearch ? `No results for "${threadSearch}"` : 'No conversations yet.'}
                  </div>;
            })()
          ) : sidebarTab === 'groups' ? (
            <>
              {filteredGroups.length > 0
                ? filteredGroups.map((t) => (
                    <ThreadRow key={t.id} thread={t} active={activeThread?.id === t.id} userStatuses={userStatuses} onClick={() => selectThread(t)} />
                  ))
                : <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: 12.5 }}>
                    {threadSearch ? `No groups matching "${threadSearch}"` : 'No groups yet. Click + Group to create one.'}
                  </div>
              }
            </>
          ) : sidebarTab === 'projects' ? (
            /* Projects tab — inline project list */
            (() => {
              const OPEN_STATUSES = ['Open', 'Launched / Field Live'];
              const visibleProjects = (showAllProjects ? projectsTab : projectsTab.filter(p => OPEN_STATUSES.includes(p.status)))
                .filter(p => !projectsTabSearch || (p.project_name || p.name || '').toLowerCase().includes(projectsTabSearch.toLowerCase()) || (p.segment_name || '').toLowerCase().includes(projectsTabSearch.toLowerCase()));
              return (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ padding: '10px 12px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                    <input value={projectsTabSearch} onChange={e => setProjectsTabSearch(e.target.value)}
                      placeholder="Search projects…"
                      style={{ flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit', marginRight: 8 }} />
                    <button onClick={() => {
                      const next = !showAllProjects;
                      setShowAllProjects(next);
                      if (projectsTab.length === 0 || next) {
                        setProjectsLoading(true);
                        const url = next ? `${API}/api/dm/projects/?all=1` : `${API}/api/dm/projects/`;
                        fetch(url, { headers: authHeader() })
                          .then(r => r.json()).then(d => { setProjectsTab(Array.isArray(d) ? d : []); })
                          .catch(() => {}).finally(() => setProjectsLoading(false));
                      }
                    }}
                      style={{ fontSize: 11, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, background: showAllProjects ? '#4f46e5' : '#f8fafc', color: showAllProjects ? '#fff' : '#64748b', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {showAllProjects ? 'Open only' : 'All projects'}
                    </button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {projectsLoading
                      ? <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>Loading…</div>
                      : visibleProjects.length === 0
                        ? <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>{projectsTabSearch ? 'No matching projects.' : 'No open projects found.'}</div>
                        : visibleProjects.map(p => {
                            const name = p.project_name || p.name || 'Unnamed';
                            const segName = p.segment_name && p.segment_name !== name ? p.segment_name : '';
                            const status = p.status || '';
                            const statusColor = status === 'Open' ? '#16a34a' : status === 'Launched / Field Live' ? '#0891b2' : '#64748b';
                            const isActive = activeProject?.id === p.id;
                            return (
                              <div key={p.id} onClick={() => { setActiveProject(p); setActiveThread(null); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: isActive ? '#eef2ff' : '' }}
                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                                  {segName && <div style={{ fontSize: 10.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{segName}</div>}
                                  {status && <div style={{ fontSize: 10.5, color: statusColor, fontWeight: 500 }}>{status}</div>}
                                </div>
                                <span style={{ fontSize: 14, color: '#94a3b8', flexShrink: 0 }}>›</span>
                              </div>
                            );
                          })
                    }
                  </div>
                </div>
              );
            })()
          ) : (
            /* People tab — ALL users, regardless of existing threads */
            <>
              {allUsers
                .filter((u) => {
                  if (threadSearch && !(u.name || '').toLowerCase().includes(threadSearch.toLowerCase()) && !(u.email || '').toLowerCase().includes(threadSearch.toLowerCase())) return false;
                  if (filterOnline && (userStatuses[u.email] || 'offline') !== 'online') return false;
                  return true;
                })
                .sort((a, b) => {
                  const aOnline = userStatuses[a.email] === 'online';
                  const bOnline = userStatuses[b.email] === 'online';
                  if (aOnline && !bOnline) return -1;
                  if (!aOnline && bOnline) return 1;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .map((u) => {
                  const uStatus = userStatuses[u.email] || 'offline';
                  const hasDM = dms.some((t) => t.participants.some((p) => p.id === u.id));
                  const statusLabel = uStatus === 'online' ? 'Online' : uStatus === 'busy' ? 'Busy' : uStatus === 'away' ? 'Away' : uStatus === 'tea_break' ? '🍵 Tea Break' : uStatus === 'lunch_break' ? '🍱 Lunch Break' : uStatus === 'see_you_tomorrow' ? '🌅 Left for the Day' : uStatus === 'on_a_break' ? '☕ On a Break' : 'Offline';
                  const statusColor = STATUS_COLOR[uStatus] || STATUS_COLOR.offline;
                  return (
                    <div key={u.id} onClick={() => !creating && openDMWith(u)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: creating ? 'wait' : 'pointer', borderRadius: 8, margin: '1px 8px', transition: 'background 0.12s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <Avatar name={u.name} size={40} status={uStatus} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: statusColor, fontWeight: 500 }}>● {statusLabel}</div>
                      </div>
                      {hasDM && <span style={{ fontSize: 10, color: '#0d9488', fontWeight: 700, background: 'rgba(20,184,166,0.12)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>DM</span>}
                    </div>
                  );
                })}
              {allUsers.filter((u) => {
                if (threadSearch && !(u.name || '').toLowerCase().includes(threadSearch.toLowerCase()) && !(u.email || '').toLowerCase().includes(threadSearch.toLowerCase())) return false;
                if (filterOnline && (userStatuses[u.email] || 'offline') !== 'online') return false;
                return true;
              }).length === 0 && (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: 12.5 }}>No users found.</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Main area ─────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0 }}>
        {!activeThread && !activeProject ? (
          /* Welcome state */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Your messages</div>
            <div style={{ fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 300 }}>
              Select a conversation from the left, or start a new one with the ✏ button.
            </div>
          </div>
        ) : activeProject ? (
          /* Project discussion panel — full Discussion page embedded */
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, background: '#fff', flexShrink: 0 }}>
              <button onClick={() => setActiveProject(null)} title="Back to projects list"
                style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0, fontSize: 15 }}>←</button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <JTDiscussionPage embeddedSegmentId={activeProject.id} embedded={true} />
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12, background: '#fff', flexShrink: 0 }}>
              {activeThread.is_group
                ? <GroupAvatar size={40} />
                : <Avatar name={activeName} size={40} status={activeStatus} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeName}</div>
                {activeThread.is_group ? (
                  <div style={{ fontSize: 11.5, color: '#64748b' }}>
                    {activeThread.participants.length + 1} members
                    {' · '}
                    {activeThread.participants.map((p) => p.name).join(', ')}
                  </div>
                ) : (
                  <div style={{ fontSize: 11.5, color: activeOnline ? '#22c55e' : '#94a3b8', fontWeight: 500 }}>
                    {activeOnline ? 'Online' : 'Offline'}
                  </div>
                )}
              </div>
              {/* Participant mini-avatars + gear for groups */}
              {activeThread.is_group && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ display: 'flex' }}>
                    {activeThread.participants.slice(0, 5).map((p) => (
                      <Avatar key={p.id} name={p.name} size={26} />
                    ))}
                    {activeThread.participants.length > 5 && (
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                        +{activeThread.participants.length - 5}
                      </div>
                    )}
                  </div>
                  {activeThread.created_by_id === activeThread.participants.find(p => p)?.id || true ? (
                    <button
                      title="Group settings"
                      onClick={() => setShowGroupSettings(v => !v)}
                      style={{ border: 'none', background: showGroupSettings ? '#f1f5f9' : 'transparent', borderRadius: 6, width: 30, height: 30, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      ⚙️
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            {/* Group settings modal */}
            {showGroupSettings && activeThread?.is_group && (() => {
              const isAdmin = activeThread.is_group_admin;
              const gs = activeThread.group_settings || {};
              const customAlerts = gs.custom_alerts || [];

              const FeatureTile = ({ featureKey, IconComp, iconColor, label, desc }) => {
                const on = !!(gs.features?.[featureKey]);
                return (
                  <button
                    disabled={!isAdmin}
                    onClick={() => isAdmin && toggleGroupFeature(featureKey, !on)}
                    style={{
                      position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                      borderRadius: 12, border: on ? '1px solid #a5b4fc' : '1px solid #e2e8f0',
                      background: on ? '#eef2ff' : '#fff', padding: 16, textAlign: 'left',
                      cursor: isAdmin ? 'pointer' : 'default', fontFamily: 'inherit',
                      transition: 'border-color .15s, background .15s', width: '100%',
                    }}
                  >
                    {on && (
                      <span style={{ position: 'absolute', top: 10, right: 10, width: 20, height: 20, background: '#4f46e5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Check size={12} color="#fff" />
                      </span>
                    )}
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, flexShrink: 0 }}>
                      <IconComp size={16} color="#fff" />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{label}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>{desc}</div>
                  </button>
                );
              };

              const ReminderTile = ({ settingKey, IconComp, iconColor, label, desc }) => {
                const saving = reminderSaving[settingKey] || '';
                const currentVal = gs[settingKey] || '';
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', padding: 14, opacity: isAdmin ? 1 : 0.55 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, flexShrink: 0 }}>
                      <IconComp size={16} color="#fff" />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{label}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3, marginBottom: 10, lineHeight: 1.4 }}>{desc}</div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
                      {saving === 'saving' && <span style={{ fontSize: 10, color: '#94a3b8' }}>saving&#8230;</span>}
                      {saving === 'saved' && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>&#10003;</span>}
                      <input
                        type="time" disabled={!isAdmin}
                        defaultValue={currentVal} key={`${activeThread.id}_${settingKey}`}
                        onChange={e => { const v = e.target.value; if (isAdmin && v && /^\d{2}:\d{2}$/.test(v)) saveReminderTime(settingKey, v); }}
                        style={{
                          flex: 1, borderRadius: 8, padding: '5px 8px', fontSize: 12,
                          border: currentVal ? '1px solid #e2e8f0' : '1px dashed #cbd5e1',
                          color: currentVal ? '#0f172a' : '#94a3b8',
                          background: isAdmin ? '#fff' : '#f8fafc',
                          cursor: isAdmin ? 'pointer' : 'not-allowed', outline: 'none', fontFamily: 'inherit',
                        }}
                      />
                    </div>
                  </div>
                );
              };

              const currentMemberIds = new Set(activeThread.participants.map(p => p.id));
              const addCandidates = memberAddSearch.trim()
                ? allUsers.filter(u => u.id !== myUserId && !currentMemberIds.has(u.id) && (u.name.toLowerCase().includes(memberAddSearch.toLowerCase()) || u.email.toLowerCase().includes(memberAddSearch.toLowerCase()))).slice(0, 5)
                : [];
              const MembersSection = () => (
                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'visible' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 600 }}>{allUsers.find(u => u.id === myUserId)?.name || 'You'} <span style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 400 }}>(you)</span></span>
                  </div>
                  {activeThread.participants.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: 12.5, color: '#374151' }}>{p.name}</span>
                      {isAdmin && (
                        <button onClick={() => removeMember(p.id)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#ef4444', fontWeight: 600, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 }}>
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );

              const navItems = [
                { key: 'features',  label: 'Features',      iconColor: '#eef2ff', iconStroke: '#4f46e5', iconPath: <><rect x="3" y="3" width="7" height="7" rx="1" stroke="#4f46e5" strokeWidth="2"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="#4f46e5" strokeWidth="2"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="#4f46e5" strokeWidth="2"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="#4f46e5" strokeWidth="2"/></> },
                { key: 'reminders', label: 'Reminders',     iconColor: '#fef3c7', iconStroke: '#f59e0b', iconPath: <><circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/><polyline points="12 6 12 12 16 14" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/></> },
                { key: 'alerts',    label: 'Custom Alerts', iconColor: '#fce7f3', iconStroke: '#ec4899', iconPath: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="#ec4899" strokeWidth="2"/></> },
                { key: 'members',   label: 'Members',       iconColor: '#f0fdf4', iconStroke: '#16a34a', iconPath: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="#16a34a" strokeWidth="2"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/></> },
              ];
              const activeTab = groupSettingsTab;
              const sectionTitles = { features: 'Group Features', reminders: 'Scheduled Reminders', alerts: 'Custom Alerts', members: `Members (${activeThread.participants.length + 1})` };

              return (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() => setShowGroupSettings(false)}>
                  <div style={{ background: '#fff', borderRadius: 16, width: 680, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'row', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden' }}
                    onClick={e => e.stopPropagation()}>

                    {/* Left nav */}
                    <div style={{ width: 186, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0, padding: '20px 0' }}>
                      <div style={{ padding: '0 16px 16px', borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeThread.group_name || 'Group'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{activeThread.participants.length + 1} members · {isAdmin ? 'Admin' : 'Member'}</div>
                      </div>
                      {navItems.map(ni => (
                        <button key={ni.key} onClick={() => { setGroupSettingsTab(ni.key); setShowAddMemberPanel(false); setMemberAddSearch(''); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', fontSize: 13, color: activeTab === ni.key ? '#4f46e5' : '#475569', fontWeight: activeTab === ni.key ? 700 : 500, borderLeft: `3px solid ${activeTab === ni.key ? '#4f46e5' : 'transparent'}`, background: activeTab === ni.key ? '#eef2ff' : 'transparent', border: 'none', borderLeft: `3px solid ${activeTab === ni.key ? '#4f46e5' : 'transparent'}`, width: '100%', textAlign: 'left', fontFamily: 'inherit', transition: 'all .12s' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, background: ni.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">{ni.iconPath}</svg>
                          </div>
                          {ni.label}
                        </button>
                      ))}
                      <div style={{ marginTop: 'auto', padding: '12px 16px 0', borderTop: '1px solid #e2e8f0' }}>
                        <button onClick={() => setShowGroupSettings(false)} style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#64748b', fontFamily: 'inherit' }}>Close</button>
                      </div>
                    </div>

                    {/* Right content */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{sectionTitles[activeTab]}</div>
                        {activeTab === 'alerts' && isAdmin && !showAddAlert && (
                          <button onClick={() => { setShowAddAlert(true); setNewAlertEmoji('🔔'); setNewAlertLabel(''); setNewAlertTime(''); setNewAlertType('daily'); setNewAlertRepeatHours('1'); setNewAlertDate(''); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#eef2ff', border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#4f46e5', cursor: 'pointer', fontFamily: 'inherit' }}>
                            <Plus size={13} /> Add alert
                          </button>
                        )}
                        {activeTab === 'members' && isAdmin && (
                          <div style={{ position: 'relative' }}>
                            <button onClick={() => { setShowAddMemberPanel(v => !v); setMemberAddSearch(''); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, background: showAddMemberPanel ? '#dcfce7' : '#f0fdf4', border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#16a34a', cursor: 'pointer', fontFamily: 'inherit' }}>
                              <Plus size={13} /> Add member
                            </button>
                            {showAddMemberPanel && (
                              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 260, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.13)', zIndex: 200, overflow: 'hidden' }}>
                                <div style={{ padding: '10px 12px 6px', borderBottom: '1px solid #f1f5f9' }}>
                                  <input autoFocus value={memberAddSearch} onChange={e => setMemberAddSearch(e.target.value)}
                                    placeholder="Search by name or email…"
                                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: '#f8fafc' }} />
                                </div>
                                {memberAddSearch.trim() && addCandidates.length === 0 && (
                                  <div style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>No users found</div>
                                )}
                                {addCandidates.map(u => (
                                  <div key={u.id} onMouseDown={() => { addMember(u.id); setShowAddMemberPanel(false); setMemberAddSearch(''); }}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{u.name}</div>
                                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{u.email}</div>
                                    </div>
                                    <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 600 }}>+ Add</span>
                                  </div>
                                ))}
                                {!memberAddSearch.trim() && (
                                  <div style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>Start typing to search…</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Scrollable body */}
                      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

                    {/* FEATURES TAB */}
                    {activeTab === 'features' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <FeatureTile featureKey="daily_task_update" IconComp={ClipboardList} iconColor="#fb923c" label="Daily Task Update" desc="Members can submit morning plans & end-of-day updates" />
                        <FeatureTile featureKey="polls_enabled" IconComp={BarChart3} iconColor="#10b981" label="Polls" desc="Members can create polls for group decisions" />
                      </div>
                    )}

                    {/* REMINDERS TAB */}
                    {activeTab === 'reminders' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <ReminderTile settingKey="tea_break_time" IconComp={Coffee} iconColor="#f59e0b" label="Tea Break" desc="Daily notification for all members" />
                        <ReminderTile settingKey="lunch_time" IconComp={Utensils} iconColor="#f43f5e" label="Lunch Break" desc="Lunch reminder for all members" />
                        <ReminderTile settingKey="eod_time" IconComp={LogOut} iconColor="#6366f1" label="Left for the Day" desc="End-of-day reminder for all members" />
                      </div>
                    )}

                    {/* ALERTS TAB */}
                    {activeTab === 'alerts' && (() => {
                      const alertFormValid = newAlertLabel.trim() && newAlertTime && (newAlertType !== 'once' || newAlertDate);
                      const pillStyle = (active) => ({ padding: '4px 12px', borderRadius: 20, border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`, background: active ? '#eef2ff' : '#fff', color: active ? '#4f46e5' : '#64748b', fontSize: 11.5, fontWeight: active ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' });
                      return (
                        <>
                          {customAlerts.map(ca => (
                            <div key={ca.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <span style={{ fontSize: 20, marginTop: 1 }}>{ca.emoji || '🔔'}</span>
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{ca.label}</div>
                                  {ca.content && <div style={{ fontSize: 12, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>{ca.content}</div>}
                                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                                    {ca.type === 'repeat' ? `Every ${ca.repeat_hours}h \xB7 from ${ca.time || '--:--'}` : ca.type === 'once' ? `One-time \xB7 ${ca.date || ''} ${ca.time || '--:--'}` : `Daily \xB7 ${ca.time || '--:--'}`}
                                  </div>
                                </div>
                              </div>
                              {isAdmin && <button onClick={() => saveCustomAlerts(customAlerts.filter(a => a.id !== ca.id))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#f87171', fontSize: 17, lineHeight: 1, padding: '2px 6px', flexShrink: 0 }}>×</button>}
                            </div>
                          ))}
                          {customAlerts.length === 0 && !showAddAlert && (
                            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>No custom alerts yet.</div>
                          )}
                          {isAdmin && showAddAlert && (
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button style={pillStyle(newAlertType === 'daily')} onClick={() => setNewAlertType('daily')}>Daily</button>
                                <button style={pillStyle(newAlertType === 'repeat')} onClick={() => setNewAlertType('repeat')}>Repeat</button>
                                <button style={pillStyle(newAlertType === 'once')} onClick={() => setNewAlertType('once')}>One-time</button>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <input type="text" value={newAlertEmoji} onChange={e => setNewAlertEmoji(e.target.value)} maxLength={4} placeholder="🔔" style={{ width: 44, padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 16, textAlign: 'center', outline: 'none', fontFamily: 'inherit', background: '#fff' }} />
                                <input type="text" value={newAlertLabel} onChange={e => setNewAlertLabel(e.target.value)} placeholder="Title&#8230;" style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' }} />
                                <input type="time" value={newAlertTime} onChange={e => setNewAlertTime(e.target.value)} style={{ width: 90, padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', background: '#fff' }} />
                              </div>
                              <textarea value={newAlertContent} onChange={e => setNewAlertContent(e.target.value)} placeholder="Message content (optional)&#8230;" rows={2} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', background: '#fff', resize: 'vertical', lineHeight: 1.5 }} />
                              {newAlertType === 'repeat' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>Repeat every</span>
                                  <select value={newAlertRepeatHours} onChange={e => setNewAlertRepeatHours(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', background: '#fff', color: '#0f172a' }}>
                                    <option value="0.5">30 minutes</option><option value="1">1 hour</option><option value="2">2 hours</option><option value="3">3 hours</option><option value="4">4 hours</option><option value="6">6 hours</option><option value="8">8 hours</option>
                                  </select>
                                  <span style={{ fontSize: 11.5, color: '#94a3b8' }}>starting from above time</span>
                                </div>
                              )}
                              {newAlertType === 'once' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>Date</span>
                                  <input type="date" value={newAlertDate} onChange={e => setNewAlertDate(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', background: '#fff', color: '#0f172a' }} />
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button onClick={() => { setShowAddAlert(false); setNewAlertContent(''); }} style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#64748b', fontFamily: 'inherit' }}>Cancel</button>
                                <button disabled={alertSaving || !alertFormValid} onClick={async () => {
                                    if (!alertFormValid) return;
                                    setAlertSaving(true);
                                    const newAlert = { id: `${Date.now()}`, emoji: newAlertEmoji || '🔔', label: newAlertLabel.trim(), content: newAlertContent.trim(), time: newAlertTime, type: newAlertType, ...(newAlertType === 'repeat' ? { repeat_hours: newAlertRepeatHours } : {}), ...(newAlertType === 'once' ? { date: newAlertDate } : {}) };
                                    await saveCustomAlerts([...customAlerts, newAlert]);
                                    setShowAddAlert(false); setNewAlertContent(''); setAlertSaving(false);
                                  }}
                                  style={{ padding: '6px 16px', border: 'none', borderRadius: 7, background: !alertFormValid ? '#f1f5f9' : '#4f46e5', color: !alertFormValid ? '#94a3b8' : '#fff', cursor: !alertFormValid ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
                                  {alertSaving ? 'Saving&#8230;' : 'Save'}
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* MEMBERS TAB */}
                    {activeTab === 'members' && <MembersSection />}

                      </div>{/* end scrollable body */}
                    </div>{/* end right content */}
                  </div>{/* end modal card */}
                </div>
              );
            })()}
            {/* Message feed */}
            <style>{`.dm-bubble table{border-collapse:collapse;font-size:13px;max-width:100%;overflow:auto;display:block}.dm-bubble td,.dm-bubble th{border:1px solid #d1d5db;padding:5px 11px;white-space:normal;word-break:break-word}.dm-bubble th{background:#f8fafc;font-weight:600}.dm-bubble-own table{border-collapse:collapse;font-size:13px;max-width:100%;overflow:auto;display:block}.dm-bubble-own td,.dm-bubble-own th{border:1px solid #a5b4fc;padding:5px 11px;white-space:normal;word-break:break-word;color:#1e1b4b}.dm-bubble-own th{background:#dde4fd;font-weight:600}.dm-bubble a{color:#4f46e5;text-decoration:none}.dm-bubble a:hover{text-decoration:underline}.dm-bubble-own a{color:#3730a3;text-decoration:none}.dm-bubble-own a:hover{text-decoration:underline}.dm-bubble img,.dm-bubble-own img{max-width:320px;max-height:320px;border-radius:8px;display:block;cursor:pointer;margin:4px 0}[contenteditable] img{max-width:320px;max-height:320px;border-radius:8px;display:block;margin:4px 0;cursor:pointer;opacity:1;transition:opacity 0.15s,outline 0.15s;outline:2px solid transparent}[contenteditable] img:hover{opacity:0.75;outline:2px solid #ef4444}`}</style>
            {lightboxSrc && (
              <div onClick={() => setLightboxSrc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
                <img src={lightboxSrc} alt="full size" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()} />
                <button onClick={() => setLightboxSrc(null)} style={{ position: 'absolute', top: 18, right: 24, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 22, width: 38, height: 38, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            )}
            <div ref={msgAreaRef} onScroll={(e) => {
              const el = e.currentTarget;
              isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
              if (el.scrollTop < 80 && hasOlderMsgs && !loadingOlder) fetchOlderMessages();
            }} onClick={(e) => { if (e.target.tagName === 'IMG') setLightboxSrc(e.target.src); }} style={{ flex: 1, overflowY: 'auto', padding: '12px 0', background: '#ffffff' }}>

              {/* Load older messages */}
              {hasOlderMsgs && (
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  <button onClick={fetchOlderMessages} disabled={loadingOlder}
                    style={{ border: 'none', background: 'none', cursor: loadingOlder ? 'default' : 'pointer', fontSize: 12, color: '#14b8a6', fontWeight: 600, fontFamily: 'inherit', padding: '4px 12px' }}>
                    {loadingOlder ? 'Loading…' : '↑ Load older messages'}
                  </button>
                </div>
              )}

              {loadingMsgs ? (
                <div style={{ textAlign: 'center', paddingTop: 40, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
              ) : messages[0]?._error ? (
                <div style={{ textAlign: 'center', paddingTop: 60 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                  <div style={{ fontSize: 13.5, color: '#64748b', marginBottom: 16 }}>Failed to load messages.</div>
                  <button onClick={() => fetchMessages(activeThread.id)}
                    style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                    Retry
                  </button>
                </div>
              ) : msgsByDay.length === 0 ? (
                <div style={{ textAlign: 'center', paddingTop: 60, color: '#94a3b8', fontSize: 13 }}>
                  No messages yet. Say hi! 👋
                </div>
              ) : (
                msgsByDay.map((item) =>
                  item.type === 'divider'
                    ? <DayDivider key={item.key} label={item.label} />
                    : <MessageBubble key={item.key} msg={item.msg}
                        onReply={(m) => { setEditingMsg(null); setReplyTo(m); setTimeout(() => inputRef.current?.focus(), 50); }}
                        onEdit={startEdit}
                        onDelete={deleteMessage}
                        onReact={toggleReaction}
                        onForward={openForward}
                        onVote={handleVote}
                        onShare={handleSharePoll}
                        myUserId={myUserId}
                        userMap={Object.fromEntries(allUsers.map(u => [String(u.id), u.name]))}
                      />
                )
              )}
              <div ref={bottomRef} />
            </div>

            {/* Reply / Edit context bar */}
            {(replyTo || editingMsg) && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', background: '#f0f9ff', borderTop: '1px solid #bae6fd', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{replyTo ? '↩' : '✏'}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>{replyTo ? `Replying to ${replyTo.sender_name}` : 'Editing message'}</div>
                    <div style={{ fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replyTo?.content || editingMsg?.content?.replace(/<[^>]+>/g, '')}</div>
                  </div>
                </div>
                <button onClick={cancelAction} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8', lineHeight: 1, padding: 4 }}>✕</button>
              </div>
            )}

            {/* My status bar — shown when not online */}
            {(() => {
              const myEmail = localStorage.getItem('username');
              const myStatus = myEmail ? (userStatuses[myEmail] || 'offline') : null;
              if (!myStatus || myStatus === 'online') return null;
              const STATUS_LABEL = { offline: 'Offline', away: 'Away', busy: 'Busy', tea_break: '🍵 Tea Break', lunch_break: '🍱 Lunch Break', see_you_tomorrow: '🌅 Left for the Day', on_a_break: '☕ On a Break' };
              const STATUS_BG = { offline: '#f1f5f9', away: '#fff7ed', busy: '#fffbeb', tea_break: '#fffbeb', lunch_break: '#fff1f2', see_you_tomorrow: '#f5f3ff', on_a_break: '#fff7ed' };
              const STATUS_CLR = { offline: '#64748b', away: '#ea580c', busy: '#d97706', tea_break: '#d97706', lunch_break: '#e11d48', see_you_tomorrow: '#7c3aed', on_a_break: '#ea580c' };
              const label = STATUS_LABEL[myStatus] || myStatus;
              const bg = STATUS_BG[myStatus] || '#f1f5f9';
              const clr = STATUS_CLR[myStatus] || '#64748b';
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 14px', background: bg, borderTop: `1px solid ${clr}22`, flexShrink: 0 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: clr, flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: clr }}>Your status: {label}</span>
                  <span style={{ fontSize: 11, color: clr, opacity: 0.7, marginLeft: 2 }}>— others can see this</span>
                </div>
              );
            })()}

            {/* Input bar */}
            <div style={{ borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0, position: 'relative' }}>
              {/* @mention dropdown */}
              {showMentions && mentionUsers.length > 0 && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px 10px 0 0', boxShadow: '0 -6px 24px rgba(0,0,0,0.1)', zIndex: 500, overflow: 'hidden' }}>
                  <div style={{ padding: '6px 14px 4px', fontSize: 10.5, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Mention someone</div>
                  {mentionUsers.map((u, i) => (
                    <div key={u.id}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                      onMouseEnter={() => setMentionIdx(i)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', background: i === mentionIdx ? '#f1f5f9' : '#fff', borderTop: '1px solid #f8fafc' }}>
                      <Avatar name={u.name} size={30} status={userStatuses[u.email] || 'offline'} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Attachment link dialog — disabled */}
              {false && showAttach && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px 10px 0 0', boxShadow: '0 -6px 24px rgba(0,0,0,0.12)', zIndex: 500, padding: '14px 16px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>📎 Add file link</div>
                  <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 10 }}>Paste a OneDrive or SharePoint <strong>web sharing link</strong> (https://...). In OneDrive, right-click the file → Share → Copy link.</div>
                  <input
                    autoFocus
                    type="text"
                    placeholder="https://4sightglobal-my.sharepoint.com/..."
                    value={attachUrl}
                    onChange={e => setAttachUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertAttachment(); } if (e.key === 'Escape') setShowAttach(false); }}
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 10 }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowAttach(false)} style={{ padding: '6px 16px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748b', fontFamily: 'inherit' }}>Cancel</button>
                    <button onClick={insertAttachment} style={{ padding: '6px 16px', border: 'none', borderRadius: 7, background: '#4f46e5', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600, fontFamily: 'inherit' }}>Attach</button>
                  </div>
                </div>
              )}
              {/* Formatting toolbar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '6px 14px 0', borderBottom: '1px solid #f1f5f9' }}>
                {[
                  { label: 'B', title: 'Bold', cmd: 'bold', style: { fontWeight: 800 } },
                  { label: 'I', title: 'Italic', cmd: 'italic', style: { fontStyle: 'italic' } },
                  { label: 'U', title: 'Underline', cmd: 'underline', style: { textDecoration: 'underline' } },
                  { label: 'S', title: 'Strikethrough', cmd: 'strikeThrough', style: { textDecoration: 'line-through' } },
                ].map(({ label, title, cmd, style }) => (
                  <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); execFmt(cmd); }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 5, width: 28, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#475569', fontFamily: 'inherit', ...style }}>
                    {label}
                  </button>
                ))}
                <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                {[
                  { label: '• List', title: 'Bullet list', cmd: 'insertUnorderedList' },
                  { label: '1. List', title: 'Numbered list', cmd: 'insertOrderedList' },
                ].map(({ label, title, cmd }) => (
                  <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); execFmt(cmd); }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 5, padding: '0 6px', height: 26, display: 'flex', alignItems: 'center', fontSize: 11.5, color: '#475569', fontFamily: 'inherit' }}>
                    {label}
                  </button>
                ))}
                <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                <button title="Code" onMouseDown={(e) => { e.preventDefault(); execFmt('formatBlock', 'pre'); }}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 5, padding: '0 6px', height: 26, fontSize: 11.5, color: '#475569', fontFamily: 'monospace' }}>
                  {'</>'}
                </button>
                <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                {/* Daily Task Update button — only in groups with feature enabled */}
                {activeThread?.is_group && activeThread?.group_settings?.features?.daily_task_update && (
                  <>
                    <button title="Morning Task Plan" onClick={() => openTaskForm('morning')}
                      style={{ border: 'none', background: (showTaskForm && taskMode === 'morning') ? '#f0fdfa' : 'transparent', cursor: 'pointer', borderRadius: 5, padding: '0 8px', height: 26, fontSize: 11.5, fontWeight: 600, color: '#0d9488', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                      📋 Task Update
                    </button>
                    <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                  </>
                )}
                {/* Poll button — only in groups with polls enabled */}
                {activeThread?.is_group && activeThread?.group_settings?.features?.polls_enabled && (
                  <>
                    <button title="Create Poll" onClick={openPollForm}
                      style={{ border: 'none', background: showPollForm ? '#f5f3ff' : 'transparent', cursor: 'pointer', borderRadius: 5, padding: '0 8px', height: 26, fontSize: 11.5, fontWeight: 600, color: '#7c3aed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                      📊 Poll
                    </button>
                    <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                  </>
                )}
                <div style={{ position: 'relative' }}>
                  <button title="Emoji" onClick={() => setShowEmoji(v => !v)}
                    style={{ border: 'none', background: showEmoji ? '#f1f5f9' : 'transparent', cursor: 'pointer', borderRadius: 5, width: 28, height: 26, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    😊
                  </button>
                  {showEmoji && (
                    <div style={{ position: 'absolute', bottom: 32, left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, width: 280 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {['😀','😂','😍','🥰','😊','😎','🤔','😢','😅','🤣','❤️','👍','👎','🙏','🎉','🔥','✅','⭐','💯','🚀','👀','💪','🤝','👋','😴','🤯','🥳','😱','🤦','🙌','💡','📌','⚠️','✔️','❌','📎','🗓️','📧','📞','💬'].map(e => (
                          <button key={e} onClick={() => {
                            inputRef.current?.focus();
                            document.execCommand('insertText', false, e);
                            setInputEmpty(false);
                            setShowEmoji(false);
                          }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, width: 34, height: 34, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onMouseEnter={ev => ev.currentTarget.style.background = '#f1f5f9'}
                            onMouseLeave={ev => ev.currentTarget.style.background = 'none'}>
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 4px' }} />
                <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                <button
                  title="Send image"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={imageUploading}
                  style={{ border: 'none', background: 'transparent', cursor: imageUploading ? 'default' : 'pointer', borderRadius: 5, width: 28, height: 26, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', opacity: imageUploading ? 0.5 : 1 }}>
                  {imageUploading ? '…' : '🖼️'}
                </button>
              </div>
              {/* Left-for-the-day notice */}
              {activeStatus === 'see_you_tomorrow' && (
                <div style={{ margin: '0 12px 6px', padding: '8px 14px', background: 'linear-gradient(90deg,rgba(55,48,163,0.08),rgba(129,140,248,0.08))', border: '1px solid rgba(129,140,248,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🌅</span>
                  <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 500 }}>{activeParticipantName || 'This person'} has left for the day — they'll see your message tomorrow.</span>
                </div>
              )}
              {/* On a break notice */}
              {activeStatus === 'on_a_break' && (
                <div style={{ margin: '0 12px 6px', padding: '8px 14px', background: 'linear-gradient(90deg,rgba(120,53,15,0.06),rgba(217,119,6,0.08))', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>☕</span>
                  <span style={{ fontSize: 13, color: '#b45309', fontWeight: 500 }}>{activeParticipantName || 'This person'} is currently on a break — they'll be back shortly.</span>
                </div>
              )}
              {/* Input + send */}
              <div style={{ padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '9px 14px', minHeight: 40, maxHeight: 150, overflowY: 'auto', position: 'relative' }}>
                  <div
                    ref={inputRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => {
                      const inner = e.currentTarget.innerHTML.replace(/^<br\s*\/?>$/i, '').trim();
                      setInputEmpty(!inner);
                      detectMention();
                    }}
                    onPaste={handlePaste}
                    onClick={(e) => {
                      if (e.target.tagName === 'IMG') {
                        e.target.remove();
                        const inner = inputRef.current?.innerHTML.replace(/^<br\s*\/?>$/i, '').trim();
                        setInputEmpty(!inner);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (showMentions && mentionUsers.length > 0) {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, mentionUsers.length - 1)); return; }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); return; }
                        if (e.key === 'Enter') { e.preventDefault(); insertMention(mentionUsers[mentionIdx]); return; }
                        if (e.key === 'Escape') { setShowMentions(false); return; }
                      }
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                    }}
                    style={{ outline: 'none', fontSize: 13.5, color: '#0f172a', fontFamily: 'inherit', lineHeight: 1.6, minHeight: 22, wordBreak: 'break-word' }}
                  />
                  {inputEmpty && (
                    <span style={{ position: 'absolute', top: 9, left: 14, color: '#94a3b8', fontSize: 13.5, pointerEvents: 'none', userSelect: 'none' }}>
                      Type a message… (Enter to send, Shift+Enter for new line)
                    </span>
                  )}
                </div>
                <button
                  onClick={sendMessage}
                  style={{
                    width: 42, height: 42, borderRadius: '50%', border: 'none', flexShrink: 0,
                    background: !inputEmpty && !sending ? '#4f46e5' : '#e2e8f0',
                    color: !inputEmpty && !sending ? '#fff' : '#94a3b8',
                    cursor: !inputEmpty && !sending ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                    transition: 'background 0.15s, color 0.15s',
                    opacity: sending ? 0.6 : 1,
                  }}
                >
                  {sending ? '…' : '➤'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Daily Task Update modal ──────────────────────────────────────────── */}
      {showTaskForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowTaskForm(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 780, maxHeight: '92vh', minHeight: 520, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: taskMode === 'eod' ? 'linear-gradient(135deg,#1e1b4b,#4f46e5)' : '#14b8a6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{taskMode === 'eod' ? '🌅' : '📋'}</span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{taskMode === 'eod' ? "Today's Summary" : 'Morning Task Plan'}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.8)', marginTop: 1 }}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}{taskMode === 'eod' ? ' — your status will be set to Left for the Day' : ''}</div>
                </div>
              </div>
              <button onClick={() => setShowTaskForm(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 18, width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            {/* Table */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e2e8f0', width: '30%' }}>Project Name</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e2e8f0' }}>{taskMode === 'eod' ? 'Task Completed' : 'Task to be done'}</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e2e8f0', width: 90 }}>{taskMode === 'eod' ? 'Hours Spent' : 'Planned Hrs'}</th>
                    {taskMode === 'eod' && <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e2e8f0', width: 110 }}>Status</th>}
                    <th style={{ padding: '8px 4px', borderBottom: '2px solid #e2e8f0', width: 28 }} />
                  </tr>
                </thead>
                <tbody>
                  {taskRows.map((row, i) => (
                    <tr key={i}>
                      <td style={{ padding: '5px 6px', borderBottom: '1px solid #f1f5f9', position: 'relative' }}>
                        <input
                          type="text"
                          value={row.project}
                          onChange={e => { updateTaskRow(i, 'project', e.target.value); setActiveProjectRow(i); setProjectSearch(e.target.value); }}
                          onFocus={() => { setActiveProjectRow(i); setProjectSearch(row.project); }}
                          onBlur={() => setTimeout(() => setActiveProjectRow(null), 200)}
                          placeholder="Type to search project…"
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                        {activeProjectRow === i && projectList.filter(p => (p.segment_name || p.project_name || '').toLowerCase().includes((projectSearch || '').toLowerCase())).length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 6, right: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 160, overflowY: 'auto' }}>
                            {projectList.filter(p => (p.segment_name || p.project_name || '').toLowerCase().includes((projectSearch || '').toLowerCase())).slice(0, 8).map((p, pi) => (
                              <div key={pi}
                                onMouseDown={() => { updateTaskRow(i, 'project', p.segment_name || p.project_name || ''); setActiveProjectRow(null); }}
                                style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 12.5, borderBottom: '1px solid #f1f5f9' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <span style={{ fontWeight: 600 }}>{p.segment_name || p.project_name}</span>
                                {p.project_name && p.segment_name && <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>{p.project_name}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '5px 6px', borderBottom: '1px solid #f1f5f9' }}>
                        <input type="text" value={row.task} onChange={e => updateTaskRow(i, 'task', e.target.value)} placeholder={taskMode === 'eod' ? 'What did you complete?' : 'What task?'}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      </td>
                      <td style={{ padding: '5px 6px', borderBottom: '1px solid #f1f5f9' }}>
                        <input type="text" value={taskMode === 'eod' ? row.hoursActual : row.hoursPlanned} onChange={e => updateTaskRow(i, taskMode === 'eod' ? 'hoursActual' : 'hoursPlanned', e.target.value)} placeholder="e.g. 2h"
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      </td>
                      {taskMode === 'eod' && (
                        <td style={{ padding: '5px 6px', borderBottom: '1px solid #f1f5f9' }}>
                          <select value={row.status} onChange={e => updateTaskRow(i, 'status', e.target.value)}
                            style={{ width: '100%', padding: '6px 6px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
                            <option>Done</option>
                            <option>In Progress</option>
                            <option>Pending</option>
                            <option>Blocked</option>
                          </select>
                        </td>
                      )}
                      <td style={{ padding: '5px 4px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                        {taskRows.length > 1 && (
                          <button onClick={() => removeTaskRow(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#f87171', fontSize: 16, lineHeight: 1 }}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addTaskRow}
                style={{ marginTop: 12, border: '1.5px dashed #14b8a6', background: 'transparent', borderRadius: 7, padding: '7px 18px', fontSize: 12.5, color: '#0d9488', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                + Add Row
              </button>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f0fdfa' }}>
              <button onClick={() => setShowTaskForm(false)} style={{ padding: '9px 22px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748b', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={submitTaskUpdate} style={{ padding: '9px 26px', border: 'none', borderRadius: 8, background: taskMode === 'eod' ? '#4f46e5' : '#14b8a6', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700, fontFamily: 'inherit' }}>
                {taskMode === 'eod' ? '🌅 Submit & Leave for the Day' : 'Submit Morning Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Poll creation modal ─────────────────────────────────────────────── */}
      {showPollForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowPollForm(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#7c3aed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>📊</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Create a Poll</span>
              </div>
              <button onClick={() => setShowPollForm(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 18, width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>QUESTION</div>
              <input value={pollQuestion} onChange={e => setPollQuestion(e.target.value)}
                placeholder="Ask a question…"
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16 }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>OPTIONS</div>
              {pollOptions.map((opt, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input value={opt} onChange={e => setPollOptions(prev => prev.map((o, idx) => idx === i ? e.target.value : o))}
                    placeholder={`Option ${i + 1}`}
                    style={{ flex: 1, padding: '8px 11px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
                  {pollOptions.length > 2 && (
                    <button onClick={() => setPollOptions(prev => prev.filter((_, idx) => idx !== i))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#f87171', lineHeight: 1, padding: 4 }}>×</button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button onClick={() => setPollOptions(prev => [...prev, ''])}
                  style={{ border: '1.5px dashed #7c3aed', background: 'transparent', borderRadius: 7, padding: '6px 16px', fontSize: 12.5, color: '#7c3aed', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                  + Add Option
                </button>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#faf5ff' }}>
              <button onClick={() => setShowPollForm(false)} style={{ padding: '8px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748b', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={submitPoll} disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
                style={{ padding: '8px 24px', border: 'none', borderRadius: 8, background: '#7c3aed', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700, fontFamily: 'inherit', opacity: (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2) ? 0.5 : 1 }}>
                Post Poll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New DM modal ─────────────────────────────────────────────────────── */}
      {modal === 'new-dm' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModal(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>New Message</span>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 20 }}>×</button>
            </div>
            <div style={{ padding: '12px 20px' }}>
              <input autoFocus type="text" placeholder="Search people…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
              {filteredUserSearch.map((u) => (
                <div key={u.id} onClick={() => !creating && openDMWith(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, cursor: creating ? 'wait' : 'pointer', marginBottom: 3 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <Avatar name={u.name} size={36} status={userStatuses[u.email] || 'offline'} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{u.name}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b' }}>{u.email}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: 11, color: userStatuses[u.email] === 'online' ? '#22c55e' : '#94a3b8', fontWeight: 600 }}>
                    {userStatuses[u.email] === 'online' ? '● Online' : '● Offline'}
                  </div>
                </div>
              ))}
              {filteredUserSearch.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>No users found.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── New Group modal ───────────────────────────────────────────────────── */}
      {modal === 'new-group' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModal(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 460, maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>New Group</span>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 20 }}>×</button>
            </div>
            <div style={{ padding: '12px 20px 0' }}>
              <input type="text" placeholder="Group name (optional)" value={groupName} onChange={(e) => setGroupName(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 10 }} />
              <input autoFocus type="text" placeholder="Search people to add…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            {groupSelected.length > 0 && (
              <div style={{ padding: '8px 20px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {groupSelected.map((u) => (
                  <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eff6ff', color: '#3730a3', borderRadius: 20, padding: '3px 10px 3px 6px', fontSize: 12.5, fontWeight: 600 }}>
                    <Avatar name={u.name} size={18} />
                    {u.name}
                    <button onClick={() => setGroupSelected(groupSelected.filter((s) => s.id !== u.id))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px' }}>
              {filteredUserSearch.map((u) => {
                const sel = groupSelected.some((s) => s.id === u.id);
                return (
                  <div key={u.id}
                    onClick={() => setGroupSelected(sel ? groupSelected.filter((s) => s.id !== u.id) : [...groupSelected, u])}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, cursor: 'pointer', marginBottom: 2, background: sel ? '#f0f4ff' : 'transparent' }}
                    onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                    <Avatar name={u.name} size={36} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: sel ? 700 : 500, color: '#0f172a' }}>{u.name}</div>
                      <div style={{ fontSize: 11.5, color: '#64748b' }}>{u.email}</div>
                    </div>
                    {sel && <span style={{ color: '#4f46e5', fontSize: 18 }}>✓</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={createGroup}
                disabled={groupSelected.length < 2 || creating}
                style={{
                  width: '100%', padding: '11px', background: groupSelected.length >= 2 ? '#4f46e5' : '#e2e8f0',
                  color: groupSelected.length >= 2 ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10,
                  cursor: groupSelected.length >= 2 ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                }}
              >
                {creating ? 'Creating…' : `Start Group Chat${groupSelected.length >= 2 ? ` (${groupSelected.length} people)` : ''}`}
              </button>
              {groupSelected.length < 2 && <div style={{ textAlign: 'center', fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>Select at least 2 people</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Forward message dialog ──────────────────────────────────────────── */}
      {forwardMsg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setForwardMsg(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Forward message</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {forwardMsg.content.replace(/<[^>]*>/g, '').trim().slice(0, 80)}
                </div>
              </div>
              <button onClick={() => setForwardMsg(null)} style={{ border: 'none', background: '#f1f5f9', cursor: 'pointer', borderRadius: 8, width: 30, height: 30, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>×</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0', padding: '0 4px' }}>
              {[['people', 'People'], ['group', 'Groups'], ['discussion', 'Discussions']].map(([tab, label]) => (
                <button key={tab} onClick={() => setForwardTab(tab)} style={{ flex: 1, padding: '10px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: forwardTab === tab ? '#0d9488' : '#64748b', borderBottom: forwardTab === tab ? '2px solid #0d9488' : '2px solid transparent', transition: 'color 0.15s' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div style={{ padding: '10px 16px 0' }}>
              <input value={forwardSearch} onChange={e => setForwardSearch(e.target.value)} placeholder={`Search ${forwardTab === 'discussion' ? 'projects' : 'conversations'}…`}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#334155' }} />
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 12px' }}>
              {forwardTab === 'people' && (() => {
                const items = threads.filter(t => !t.is_group && t.participants[0]?.name?.toLowerCase().includes(forwardSearch.toLowerCase()));
                return items.length === 0 ? <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 24 }}>No conversations found</div>
                  : items.map(t => {
                    const name = t.participants[0]?.name || 'Unknown';
                    return (
                      <button key={t.id} disabled={forwarding} onClick={() => doForward({ type: 'thread', id: t.id })}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0d9488', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                          {name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{name}</div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{t.participants[0]?.email || ''}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#0d9488', fontWeight: 600 }}>→ Send</div>
                      </button>
                    );
                  });
              })()}

              {forwardTab === 'group' && (() => {
                const items = threads.filter(t => t.is_group && (t.group_name || '').toLowerCase().includes(forwardSearch.toLowerCase()));
                return items.length === 0 ? <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 24 }}>No groups found</div>
                  : items.map(t => {
                    const name = t.group_name || t.participants.map(p => p.name).join(', ');
                    return (
                      <button key={t.id} disabled={forwarding} onClick={() => doForward({ type: 'thread', id: t.id })}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#64748b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                          👥
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{t.participants.length} members</div>
                        </div>
                        <div style={{ flexShrink: 0, fontSize: 12, color: '#0d9488', fontWeight: 600 }}>→ Send</div>
                      </button>
                    );
                  });
              })()}

              {forwardTab === 'discussion' && (() => {
                if (forwardSegmentsLoading) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b', fontSize: 13 }}>Loading projects…</div>;
                const items = forwardSegments.filter(s => (s.segment_name || s.project_name || '').toLowerCase().includes(forwardSearch.toLowerCase()));
                return items.length === 0 ? <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: 24 }}>No projects found</div>
                  : items.map(s => {
                    const name = s.segment_name || s.project_name || `Segment #${s.id}`;
                    return (
                      <button key={s.id} disabled={forwarding} onClick={() => doForward({ type: 'discussion', id: s.id })}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                          💬
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          {s.project_name && s.segment_name && <div style={{ fontSize: 11.5, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.project_name}</div>}
                        </div>
                        <div style={{ flexShrink: 0, fontSize: 12, color: '#0d9488', fontWeight: 600 }}>→ Post</div>
                      </button>
                    );
                  });
              })()}
            </div>

            {forwarding && (
              <div style={{ padding: '10px 20px', borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: 13, color: '#64748b' }}>Forwarding…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
